import React, { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { 
  Shield, CheckCircle, XCircle, Clock, Wallet, 
  ExternalLink, Copy, Check, RefreshCw, AlertCircle,
  Bell, Users, DollarSign, Lock, Eye, EyeOff,
  Mail, MessageSquare, GraduationCap, CreditCard,
  UserCheck, UserX, Trash2, FileText, QrCode,
  ChevronDown, ChevronUp, Search, Filter, Download, Send,
  Map, Key, LogOut, X, ZoomIn, Phone, FileSpreadsheet, History,
  Plus, Edit, Video, Image, File, Globe, Calendar,
  BarChart3, Monitor, Smartphone, Tablet, MapPin, Activity, BellRing
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { RichTextEditor } from '../components/RichTextEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admin Session Storage Keys
const ADMIN_TOKEN_KEY = "oar_admin_token";
const ADMIN_SESSION_KEY = "oar_admin_authenticated";

// Logo Journée Russophonie
const JOURNEE_LOGO = "https://static.prod-images.emergentagent.com/jobs/a27b2f16-dbd8-4903-b7cf-e0c3f4166774/images/def00b15247db958e0a1a006c0644ef388493b1419b5bc5be44742dac8edad62.png";

// OAR Logo
const OAR_LOGO = "https://customer-assets.emergentagent.com/job_a27b2f16-dbd8-4903-b7cf-e0c3f4166774/artifacts/zuafya4h_LOGO%20OAR%20jpeg.jpg";

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  // Password change states
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Password reset states
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [resetStep, setResetStep] = useState('email'); // 'email' or 'token'
  
  // Dashboard state
  const [activeSection, setActiveSection] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState(null);
  
  // Data states
  const [members, setMembers] = useState([]);
  const [applications, setApplications] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [cryptoPayments, setCryptoPayments] = useState([]);
  const [cryptoHistory, setCryptoHistory] = useState([]);
  const [emails, setEmails] = useState([]);
  const [recipientsCounts, setRecipientsCounts] = useState({});
  const [membersByCountry, setMembersByCountry] = useState(null);
  const [publications, setPublications] = useState([]);
  const [visitStats, setVisitStats] = useState(null);
  const [recentVisits, setRecentVisits] = useState([]);
  
  // UI states
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [copiedText, setCopiedText] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [mapFilter, setMapFilter] = useState('all'); // 'all', 'standard', 'premium'
  
  // Preview states
  const [previewMember, setPreviewMember] = useState(null);
  const [previewType, setPreviewType] = useState(null); // 'certificate' or 'card'
  
  // Mass Email states
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailTarget, setEmailTarget] = useState('newsletter');
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Publication form states
  const [showPublicationForm, setShowPublicationForm] = useState(false);
  const [editingPublication, setEditingPublication] = useState(null);
  const [publicationForm, setPublicationForm] = useState({
    title_fr: '',
    title_en: '',
    title_ru: '',
    slug: '',
    excerpt_fr: '',
    excerpt_en: '',
    excerpt_ru: '',
    content_fr: '',
    content_en: '',
    content_ru: '',
    category: 'article',
    publication_type: 'article',
    image_url: '',
    video_url: '',
    attachments: [],
    author: '',
    is_published: true
  });
  const [savingPublication, setSavingPublication] = useState(false);

  // Push notification states
  const [pushTitle, setPushTitle] = useState('');
  const [pushMessage, setPushMessage] = useState('');
  const [pushUrl, setPushUrl] = useState('');
  const [sendingPush, setSendingPush] = useState(false);
  const [pushHistory, setPushHistory] = useState([]);
  const [pushStats, setPushStats] = useState(null);

  // Contact reply states
  const [replyModal, setReplyModal] = useState({ open: false, contact: null });
  const [replySubject, setReplySubject] = useState('');
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  // Get auth header
  const getAuthHeader = () => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    // Check for existing valid token
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (token) {
      verifyToken(token);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      await axios.get(`${API}/admin/verify-token`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsAuthenticated(true);
    } catch (error) {
      // Token invalid, clear it
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      setIsAuthenticated(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchDashboardData();
    }
  }, [isAuthenticated, activeSection]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setLoginError('');
    
    try {
      const response = await axios.post(`${API}/admin/login`, {
        password: passwordInput
      });
      
      const { access_token } = response.data;
      localStorage.setItem(ADMIN_TOKEN_KEY, access_token);
      setIsAuthenticated(true);
      setPasswordInput('');
      toast.success('Connexion réussie !');
    } catch (error) {
      const message = error.response?.data?.detail || 'Mot de passe incorrect';
      setLoginError(message);
      toast.error(message);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setIsAuthenticated(false);
    toast.success('Déconnexion réussie');
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API}/admin/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, { headers: getAuthHeader() });
      
      toast.success('Mot de passe modifié avec succès !');
      setShowPasswordChange(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement');
    } finally {
      setChangingPassword(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    if (resetStep === 'email') {
      try {
        await axios.post(`${API}/admin/request-password-reset`, { email: resetEmail });
        toast.success('Code de réinitialisation envoyé par email');
        setResetStep('token');
      } catch (error) {
        toast.error('Erreur lors de l\'envoi');
      }
    } else {
      if (newPassword !== confirmPassword) {
        toast.error('Les mots de passe ne correspondent pas');
        return;
      }
      try {
        await axios.post(`${API}/admin/reset-password`, {
          token: resetToken,
          new_password: newPassword
        });
        toast.success('Mot de passe réinitialisé avec succès !');
        setShowPasswordReset(false);
        setResetStep('email');
        setResetEmail('');
        setResetToken('');
        setNewPassword('');
        setConfirmPassword('');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Token invalide ou expiré');
      }
    }
  };

  const fetchDashboardData = async () => {
    setIsLoading(true);
    try {
      const headers = getAuthHeader();
      const [statsRes, membersRes, appsRes, contactsRes, newsletterRes, cryptoRes, cryptoHistoryRes, emailsRes, recipientsRes, mapRes, publicationsRes, visitStatsRes, recentVisitsRes, pushHistoryRes, pushStatsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/members?limit=50`, { headers }),
        axios.get(`${API}/admin/applications?limit=50`, { headers }),
        axios.get(`${API}/admin/contacts?limit=50`, { headers }),
        axios.get(`${API}/admin/newsletter?limit=50`, { headers }),
        axios.get(`${API}/admin/crypto-payments?status=pending_verification`, { headers }),
        axios.get(`${API}/admin/crypto-payments/history`, { headers }),
        axios.get(`${API}/admin/emails?limit=50`, { headers }),
        axios.get(`${API}/admin/recipients-count`, { headers }),
        axios.get(`${API}/admin/members-by-country`, { headers }),
        axios.get(`${API}/admin/publications`, { headers }),
        axios.get(`${API}/admin/visits/stats`, { headers }),
        axios.get(`${API}/admin/visits/recent?limit=50`, { headers }),
        axios.get(`${API}/admin/push-notifications`, { headers }),
        axios.get(`${API}/admin/onesignal-stats`, { headers })
      ]);
      
      setStats(statsRes.data);
      setMembers(membersRes.data);
      setApplications(appsRes.data);
      setContacts(contactsRes.data);
      setNewsletter(newsletterRes.data);
      setCryptoPayments(cryptoRes.data);
      setCryptoHistory(cryptoHistoryRes.data);
      setEmails(emailsRes.data);
      setRecipientsCounts(recipientsRes.data);
      setMembersByCountry(mapRes.data);
      setPublications(publicationsRes.data);
      setVisitStats(visitStatsRes.data);
      setRecentVisits(recentVisitsRes.data);
      setPushHistory(pushHistoryRes.data);
      setPushStats(pushStatsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        handleLogout();
        toast.error('Session expirée, veuillez vous reconnecter');
      } else {
        toast.error('Erreur lors du chargement');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const sendMassEmail = async () => {
    if (!emailSubject.trim() || !emailMessage.trim()) {
      toast.error('Veuillez remplir le sujet et le message');
      return;
    }
    
    setSendingEmail(true);
    try {
      const response = await axios.post(`${API}/admin/send-mass-email`, {
        subject: emailSubject,
        message: emailMessage,
        target: emailTarget
      });
      
      toast.success(`Email envoyé à ${response.data.recipients_count} destinataires !`);
      setEmailSubject('');
      setEmailMessage('');
      fetchDashboardData(); // Refresh data
    } catch (error) {
      console.error('Error sending mass email:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingEmail(false);
    }
  };

  const sendPushNotification = async () => {
    if (!pushTitle.trim() || !pushMessage.trim()) {
      toast.error('Veuillez remplir le titre et le message');
      return;
    }
    
    setSendingPush(true);
    try {
      const headers = getAuthHeader();
      const response = await axios.post(`${API}/admin/send-push-notification`, {
        title: pushTitle,
        message: pushMessage,
        url: pushUrl || null
      }, { headers });
      
      if (response.data.success) {
        toast.success(`Notification envoyée à ${response.data.recipients || 0} abonnés !`);
        setPushTitle('');
        setPushMessage('');
        setPushUrl('');
        fetchDashboardData(); // Refresh data
      } else {
        toast.error(response.data.message || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Error sending push notification:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingPush(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedText(text);
    toast.success('Copié !');
    setTimeout(() => setCopiedText(null), 2000);
  };

  // Member actions
  const activateMember = async (memberId) => {
    setProcessingId(memberId);
    try {
      await axios.put(`${API}/admin/members/${memberId}/activate`);
      toast.success('Membre activé');
      fetchDashboardData();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setProcessingId(null);
    }
  };

  const deactivateMember = async (memberId) => {
    setProcessingId(memberId);
    try {
      await axios.put(`${API}/admin/members/${memberId}/deactivate`);
      toast.success('Membre désactivé');
      fetchDashboardData();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setProcessingId(null);
    }
  };

  const deleteMember = async (memberId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce membre ?')) return;
    try {
      await axios.delete(`${API}/admin/members/${memberId}`);
      toast.success('Membre supprimé');
      fetchDashboardData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Application actions
  const updateApplicationStatus = async (appId, status) => {
    try {
      await axios.put(`${API}/admin/applications/${appId}/status?status=${status}`);
      toast.success(`Statut mis à jour: ${status}`);
      fetchDashboardData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Contact actions
  const markContactRead = async (contactId) => {
    try {
      await axios.put(`${API}/admin/contacts/${contactId}/mark-read`);
      toast.success('Marqué comme lu');
      fetchDashboardData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    try {
      await axios.delete(`${API}/admin/contacts/${contactId}`);
      toast.success('Message supprimé');
      fetchDashboardData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const openReplyModal = (contact) => {
    setReplyModal({ open: true, contact });
    setReplySubject(`Re: ${contact.subject}`);
    setReplyMessage('');
  };

  const closeReplyModal = () => {
    setReplyModal({ open: false, contact: null });
    setReplySubject('');
    setReplyMessage('');
  };

  const sendContactReply = async () => {
    if (!replySubject.trim() || !replyMessage.trim()) {
      toast.error('Veuillez remplir le sujet et le message');
      return;
    }
    
    setSendingReply(true);
    try {
      const headers = getAuthHeader();
      await axios.post(`${API}/admin/contacts/${replyModal.contact.id}/reply`, {
        subject: replySubject,
        message: replyMessage
      }, { headers });
      
      toast.success(`Réponse envoyée à ${replyModal.contact.email}`);
      closeReplyModal();
      // Mark as read after reply
      await markContactRead(replyModal.contact.id);
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingReply(false);
    }
  };

  // Crypto payment actions
  const verifyCrypto = async (paymentId) => {
    setProcessingId(paymentId);
    try {
      await axios.post(`${API}/admin/crypto-payments/${paymentId}/verify`);
      toast.success('Paiement vérifié !');
      fetchDashboardData();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setProcessingId(null);
    }
  };

  const rejectCrypto = async (paymentId) => {
    const reason = prompt('Raison du rejet:');
    if (!reason) return;
    setProcessingId(paymentId);
    try {
      await axios.post(`${API}/admin/crypto-payments/${paymentId}/reject?reason=${encodeURIComponent(reason)}`);
      toast.success('Paiement rejeté');
      fetchDashboardData();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setProcessingId(null);
    }
  };

  // Newsletter actions
  const deleteSubscriber = async (subscriberId) => {
    if (!window.confirm('Supprimer cet abonné ?')) return;
    try {
      await axios.delete(`${API}/admin/newsletter/${subscriberId}`);
      toast.success('Abonné supprimé');
      fetchDashboardData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Publication actions
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const resetPublicationForm = () => {
    setPublicationForm({
      title_fr: '',
      title_en: '',
      title_ru: '',
      slug: '',
      excerpt_fr: '',
      excerpt_en: '',
      excerpt_ru: '',
      content_fr: '',
      content_en: '',
      content_ru: '',
      category: 'article',
      publication_type: 'article',
      image_url: '',
      video_url: '',
      attachments: [],
      author: '',
      is_published: true
    });
    setEditingPublication(null);
  };

  const openPublicationForm = (publication = null) => {
    if (publication) {
      setEditingPublication(publication);
      setPublicationForm({
        title_fr: publication.title_fr || '',
        title_en: publication.title_en || '',
        title_ru: publication.title_ru || '',
        slug: publication.slug || '',
        excerpt_fr: publication.excerpt_fr || '',
        excerpt_en: publication.excerpt_en || '',
        excerpt_ru: publication.excerpt_ru || '',
        content_fr: publication.content_fr || '',
        content_en: publication.content_en || '',
        content_ru: publication.content_ru || '',
        category: publication.category || 'article',
        publication_type: publication.publication_type || 'article',
        image_url: publication.image_url || '',
        video_url: publication.video_url || '',
        attachments: publication.attachments || [],
        author: publication.author || '',
        is_published: publication.is_published !== false
      });
    } else {
      resetPublicationForm();
    }
    setShowPublicationForm(true);
  };

  const savePublication = async () => {
    if (!publicationForm.title_fr.trim() || !publicationForm.author.trim()) {
      toast.error('Le titre (FR) et l\'auteur sont obligatoires');
      return;
    }

    const slug = publicationForm.slug || generateSlug(publicationForm.title_fr);
    
    setSavingPublication(true);
    try {
      const headers = getAuthHeader();
      const data = { ...publicationForm, slug };
      
      if (editingPublication) {
        await axios.put(`${API}/admin/publications/${editingPublication.id}`, data, { headers });
        toast.success('Publication mise à jour');
      } else {
        await axios.post(`${API}/admin/publications`, data, { headers });
        toast.success('Publication créée');
      }
      
      setShowPublicationForm(false);
      resetPublicationForm();
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingPublication(false);
    }
  };

  const deletePublication = async (publicationId) => {
    if (!window.confirm('Supprimer cette publication ?')) return;
    try {
      const headers = getAuthHeader();
      await axios.delete(`${API}/admin/publications/${publicationId}`, { headers });
      toast.success('Publication supprimée');
      fetchDashboardData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const togglePublishStatus = async (publicationId) => {
    try {
      const headers = getAuthHeader();
      const response = await axios.post(`${API}/admin/publications/${publicationId}/toggle-publish`, {}, { headers });
      toast.success(response.data.message);
      fetchDashboardData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const addAttachment = () => {
    const name = prompt('Nom du fichier:');
    if (!name) return;
    const url = prompt('URL du fichier (PDF, image, etc.):');
    if (!url) return;
    const type = url.toLowerCase().includes('.pdf') ? 'pdf' : 
                 url.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)/) ? 'image' : 'document';
    
    setPublicationForm(prev => ({
      ...prev,
      attachments: [...prev.attachments, { name, url, type }]
    }));
  };

  const removeAttachment = (index) => {
    setPublicationForm(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const sections = [
    { key: 'overview', label: 'Vue d\'ensemble', icon: Shield },
    { key: 'analytics', label: 'Statistiques Visites', icon: BarChart3 },
    { key: 'members', label: 'Membres', icon: Users, count: stats?.members?.total || 0 },
    { key: 'map', label: 'Carte des Membres', icon: Map },
    { key: 'applications', label: 'Candidatures', icon: GraduationCap, count: stats?.applications?.total || 0 },
    { key: 'contacts', label: 'Messages', icon: MessageSquare, count: stats?.contacts?.unread || 0 },
    { key: 'newsletter', label: 'Newsletter', icon: Mail, count: stats?.newsletter?.total || 0 },
    { key: 'publications', label: 'Publications', icon: FileText, count: stats?.publications?.total || 0 },
    { key: 'push', label: 'Notifications Push', icon: BellRing },
    { key: 'crypto', label: 'Paiements Crypto', icon: Wallet, count: stats?.crypto_pending || 0 },
    { key: 'emails', label: 'Emails', icon: Mail, count: stats?.emails?.pending || 0 },
    { key: 'assets', label: 'Ressources', icon: FileText },
    { key: 'settings', label: 'Paramètres', icon: Key },
  ];

  // Login Screen
  if (!isAuthenticated) {
    return (
      <Layout>
        <section className="min-h-screen flex items-center justify-center bg-oar-blue-dark py-24">
          <div className="max-w-md w-full mx-4">
            {/* Password Reset Modal */}
            {showPasswordReset && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading text-lg text-oar-text-primary">
                      Réinitialiser le mot de passe
                    </h3>
                    <button onClick={() => { setShowPasswordReset(false); setResetStep('email'); }}>
                      <X className="w-5 h-5 text-oar-text-muted" />
                    </button>
                  </div>
                  
                  <form onSubmit={handlePasswordReset} className="space-y-4">
                    {resetStep === 'email' ? (
                      <div>
                        <label className="block text-sm text-oar-text-secondary mb-1">Email administrateur</label>
                        <input
                          type="email"
                          value={resetEmail}
                          onChange={(e) => setResetEmail(e.target.value)}
                          placeholder="emileparfait2003@gmail.com"
                          className="w-full border border-oar-border rounded-lg px-4 py-2"
                          required
                        />
                        <p className="text-xs text-oar-text-muted mt-2">
                          Un code de réinitialisation sera envoyé à cette adresse.
                        </p>
                      </div>
                    ) : (
                      <>
                        <div>
                          <label className="block text-sm text-oar-text-secondary mb-1">Code de réinitialisation</label>
                          <input
                            type="text"
                            value={resetToken}
                            onChange={(e) => setResetToken(e.target.value)}
                            placeholder="Entrez le code reçu par email"
                            className="w-full border border-oar-border rounded-lg px-4 py-2"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-oar-text-secondary mb-1">Nouveau mot de passe</label>
                          <input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Minimum 8 caractères"
                            className="w-full border border-oar-border rounded-lg px-4 py-2"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-oar-text-secondary mb-1">Confirmer le mot de passe</label>
                          <input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Retapez le mot de passe"
                            className="w-full border border-oar-border rounded-lg px-4 py-2"
                            required
                          />
                        </div>
                      </>
                    )}
                    <button
                      type="submit"
                      className="w-full bg-oar-blue hover:bg-oar-blue-dark text-white rounded-lg px-4 py-2"
                    >
                      {resetStep === 'email' ? 'Envoyer le code' : 'Réinitialiser'}
                    </button>
                  </form>
                </div>
              </div>
            )}
            
            <div className="bg-white rounded-lg shadow-2xl p-8">
              <div className="text-center mb-8">
                <img src={OAR_LOGO} alt="OAR" className="w-20 h-20 mx-auto mb-4 rounded-full shadow-lg" />
                <h1 className="font-heading text-2xl text-oar-text-primary mb-2">
                  Administration OAR
                </h1>
                <p className="text-oar-text-muted text-sm">
                  Dashboard sécurisé avec authentification JWT
                </p>
              </div>

              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                  <label className="block text-oar-text-secondary text-sm mb-2">
                    Mot de passe administrateur
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={passwordInput}
                      onChange={(e) => setPasswordInput(e.target.value)}
                      placeholder="Entrez le mot de passe"
                      data-testid="admin-password-input"
                      className="w-full bg-oar-bg-secondary border border-oar-border focus:border-oar-blue text-oar-text-primary rounded-lg px-4 py-3 pr-12 focus:outline-none focus:ring-2 focus:ring-oar-blue/20"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-oar-text-muted hover:text-oar-blue"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {loginError && (
                    <p className="text-oar-red text-sm mt-2 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {loginError}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoggingIn}
                  data-testid="admin-login-btn"
                  className="w-full flex items-center justify-center gap-2 bg-oar-blue hover:bg-oar-blue-dark disabled:bg-gray-400 text-white rounded-lg px-6 py-3 font-medium transition-all"
                >
                  {isLoggingIn ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <Shield className="w-5 h-5" />
                  )}
                  {isLoggingIn ? 'Connexion...' : 'Accéder au dashboard'}
                </button>
                
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowPasswordReset(true)}
                    className="text-oar-blue hover:text-oar-blue-dark text-sm"
                  >
                    Mot de passe oublié ?
                  </button>
                </div>
              </form>
              
              <div className="mt-6 pt-6 border-t border-oar-border">
                <p className="text-xs text-oar-text-muted text-center">
                  Session sécurisée • Token JWT valide 24h
                </p>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  // Main Dashboard
  return (
    <Layout>
      <div className="min-h-screen bg-oar-bg-secondary">
        {/* Preview Modal */}
        {previewMember && previewType && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
              <div className="flex items-center justify-between p-4 border-b border-oar-border">
                <h3 className="font-heading text-lg text-oar-text-primary">
                  {previewType === 'certificate' ? 'Aperçu du Certificat' : 'Aperçu de la Carte de Membre'}
                </h3>
                <button onClick={() => { setPreviewMember(null); setPreviewType(null); }}>
                  <X className="w-5 h-5 text-oar-text-muted" />
                </button>
              </div>
              <div className="p-6">
                {previewType === 'certificate' ? (
                  <div className="bg-[#F5F0E6] border-2 border-oar-gold rounded-lg p-8 text-center relative">
                    {/* Watermark */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-10 pointer-events-none">
                      <img src={OAR_LOGO} alt="" className="w-64 h-64 object-contain" />
                    </div>
                    {/* Certificate Content */}
                    <img src={OAR_LOGO} alt="OAR" className="w-16 h-16 absolute top-6 left-6 rounded-lg" />
                    <p className="text-oar-blue font-bold text-xl mb-1">ORGANISATION AFRICAINE DE LA RUSSOPHONIE</p>
                    <p className="text-xs text-oar-text-muted mb-4">African Organization of Russophony • Африканская организация русскоязычия</p>
                    <p className="text-oar-red font-bold text-lg mb-4">CERTIFICAT DE MEMBRE</p>
                    <div className="h-px bg-oar-gold w-1/3 mx-auto mb-4"></div>
                    <p className="text-oar-text-muted text-sm mb-2">Nous certifions que</p>
                    <p className="text-oar-blue font-bold text-2xl mb-2">
                      {previewMember.first_name} {previewMember.last_name}
                    </p>
                    <p className="text-oar-orange font-mono font-bold text-lg mb-2">
                      Matricule: {previewMember.matricule || previewMember.member_number}
                    </p>
                    <p className={`font-bold ${previewMember.membership_type === 'premium' ? 'text-oar-red' : 'text-oar-blue'}`}>
                      MEMBRE {previewMember.membership_type?.toUpperCase()}
                    </p>
                    <p className="text-oar-text-secondary text-sm mt-4">
                      {previewMember.city ? `${previewMember.city}, ` : ''}{previewMember.country}
                    </p>
                  </div>
                ) : (
                  <div className="bg-gradient-to-br from-oar-blue to-oar-blue-dark text-white rounded-lg p-6 max-w-md mx-auto relative">
                    <img src={OAR_LOGO} alt="OAR" className="absolute top-4 left-4 w-12 h-12 rounded-full bg-white p-1" />
                    <div className="flex items-center justify-between mb-6 ml-16">
                      <div>
                        <p className="text-oar-gold font-bold text-lg">OAR</p>
                        <p className="text-xs text-white/80">Organisation Africaine de la Russophonie</p>
                      </div>
                      <span className={`px-3 py-1 rounded text-xs font-bold ${previewMember.membership_type === 'premium' ? 'bg-oar-red' : 'bg-green-600'}`}>
                        {previewMember.membership_type?.toUpperCase()}
                      </span>
                    </div>
                    <p className="font-heading text-2xl mb-1">
                      {previewMember.first_name} {previewMember.last_name}
                    </p>
                    <p className="text-oar-orange font-mono font-bold text-lg mb-4">
                      {previewMember.matricule || previewMember.member_number}
                    </p>
                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-xs text-oar-gold">PAYS</p>
                        <p className="text-sm">{previewMember.city ? `${previewMember.city}, ` : ''}{previewMember.country}</p>
                      </div>
                      <div className="w-20 h-20 bg-white rounded flex items-center justify-center">
                        <QrCode className="w-16 h-16 text-oar-blue" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-center gap-4 p-4 border-t border-oar-border">
                <a
                  href={`${API}/members/${previewMember.id}/certificate`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-oar-blue hover:bg-oar-blue-dark text-white px-4 py-2 rounded-lg"
                >
                  <Download className="w-4 h-4" />
                  Télécharger Certificat PDF
                </a>
                <a
                  href={`${API}/members/${previewMember.id}/card`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-oar-orange hover:bg-oar-orange-dark text-white px-4 py-2 rounded-lg"
                >
                  <Download className="w-4 h-4" />
                  Télécharger Carte PNG
                </a>
              </div>
            </div>
          </div>
        )}
        
        {/* Header */}
        <header className="bg-oar-blue-dark py-6">
          <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src={OAR_LOGO} alt="OAR" className="w-12 h-12 rounded-full" />
              <div>
                <h1 className="font-heading text-2xl text-white">Dashboard Admin OAR</h1>
                <p className="text-gray-400 text-sm">Gestion complète du site</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={fetchDashboardData}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg transition-all"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                Actualiser
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 bg-oar-red/20 hover:bg-oar-red/30 text-white px-4 py-2 rounded-lg transition-all"
              >
                <LogOut className="w-4 h-4" />
                Déconnexion
              </button>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex gap-8">
            {/* Sidebar */}
            <aside className="w-64 flex-shrink-0">
              <nav className="bg-white rounded-lg border border-oar-border shadow-sm overflow-hidden">
                {sections.map((section) => (
                  <button
                    key={section.key}
                    onClick={() => setActiveSection(section.key)}
                    className={`w-full flex items-center justify-between px-4 py-3 transition-all ${
                      activeSection === section.key
                        ? 'bg-oar-blue text-white'
                        : 'text-oar-text-secondary hover:bg-oar-bg-secondary'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <section.icon className="w-5 h-5" />
                      <span className="text-sm font-medium">{section.label}</span>
                    </div>
                    {section.count !== undefined && section.count > 0 && (
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        activeSection === section.key
                          ? 'bg-white/20'
                          : 'bg-oar-orange text-white'
                      }`}>
                        {section.count}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </aside>

            {/* Main Content */}
            <main className="flex-1">
              {isLoading && activeSection === 'overview' ? (
                <div className="flex items-center justify-center py-24">
                  <RefreshCw className="w-8 h-8 text-oar-blue animate-spin" />
                </div>
              ) : (
                <>
                  {/* Overview */}
                  {activeSection === 'overview' && stats && (
                    <div className="space-y-6">
                      {/* Stats Grid */}
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-white border border-oar-border rounded-lg p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-oar-blue-soft rounded-lg flex items-center justify-center">
                              <Users className="w-5 h-5 text-oar-blue" />
                            </div>
                            <div>
                              <p className="text-oar-text-muted text-xs">Membres actifs</p>
                              <p className="text-2xl font-heading font-bold text-oar-blue">{stats.members?.total || 0}</p>
                            </div>
                          </div>
                          <div className="mt-2 text-xs text-oar-text-muted">
                            Standard: {stats.members?.standard || 0} | Premium: {stats.members?.premium || 0}
                          </div>
                        </div>
                        
                        <div className="bg-white border border-oar-border rounded-lg p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-oar-green-soft rounded-lg flex items-center justify-center">
                              <GraduationCap className="w-5 h-5 text-oar-green" />
                            </div>
                            <div>
                              <p className="text-oar-text-muted text-xs">Candidatures</p>
                              <p className="text-2xl font-heading font-bold text-oar-green">{stats.applications?.total || 0}</p>
                            </div>
                          </div>
                          <div className="mt-2 text-xs text-oar-text-muted">
                            En attente: {stats.applications?.pending || 0}
                          </div>
                        </div>
                        
                        <div className="bg-white border border-oar-border rounded-lg p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-oar-orange-soft rounded-lg flex items-center justify-center">
                              <MessageSquare className="w-5 h-5 text-oar-orange" />
                            </div>
                            <div>
                              <p className="text-oar-text-muted text-xs">Messages</p>
                              <p className="text-2xl font-heading font-bold text-oar-orange">{stats.contacts?.total || 0}</p>
                            </div>
                          </div>
                          <div className="mt-2 text-xs text-oar-text-muted">
                            Non lus: {stats.contacts?.unread || 0}
                          </div>
                        </div>
                        
                        <div className="bg-white border border-oar-border rounded-lg p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-oar-red-soft rounded-lg flex items-center justify-center">
                              <Mail className="w-5 h-5 text-oar-red" />
                            </div>
                            <div>
                              <p className="text-oar-text-muted text-xs">Newsletter</p>
                              <p className="text-2xl font-heading font-bold text-oar-red">{stats.newsletter?.total || 0}</p>
                            </div>
                          </div>
                          <div className="mt-2 text-xs text-oar-text-muted">
                            Actifs: {stats.newsletter?.active || 0}
                          </div>
                        </div>
                      </div>

                      {/* Pending Crypto Alert */}
                      {stats.crypto_pending > 0 && (
                        <div className="bg-oar-orange-soft border border-oar-orange/30 rounded-lg p-4 flex items-center gap-4">
                          <Wallet className="w-8 h-8 text-oar-orange" />
                          <div className="flex-1">
                            <p className="text-oar-orange font-medium">
                              {stats.crypto_pending} paiement(s) crypto en attente de vérification
                            </p>
                            <p className="text-oar-text-muted text-sm">
                              Cliquez sur "Paiements Crypto" pour vérifier les transactions.
                            </p>
                          </div>
                          <button
                            onClick={() => setActiveSection('crypto')}
                            className="bg-oar-orange text-white px-4 py-2 rounded-lg hover:bg-oar-orange-dark transition-all"
                          >
                            Vérifier
                          </button>
                        </div>
                      )}

                      {/* Quick Actions */}
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <button
                          onClick={() => setActiveSection('members')}
                          className="bg-white border border-oar-border rounded-lg p-4 hover:border-oar-blue transition-all text-left"
                        >
                          <Users className="w-6 h-6 text-oar-blue mb-2" />
                          <p className="font-medium text-oar-text-primary">Gérer les membres</p>
                          <p className="text-xs text-oar-text-muted">Voir, activer, désactiver</p>
                        </button>
                        <button
                          onClick={() => setActiveSection('applications')}
                          className="bg-white border border-oar-border rounded-lg p-4 hover:border-oar-green transition-all text-left"
                        >
                          <GraduationCap className="w-6 h-6 text-oar-green mb-2" />
                          <p className="font-medium text-oar-text-primary">Candidatures étudiants</p>
                          <p className="text-xs text-oar-text-muted">Examiner, approuver</p>
                        </button>
                        <button
                          onClick={() => setActiveSection('contacts')}
                          className="bg-white border border-oar-border rounded-lg p-4 hover:border-oar-orange transition-all text-left"
                        >
                          <MessageSquare className="w-6 h-6 text-oar-orange mb-2" />
                          <p className="font-medium text-oar-text-primary">Messages reçus</p>
                          <p className="text-xs text-oar-text-muted">Consulter, répondre</p>
                        </button>
                        <button
                          onClick={() => setActiveSection('assets')}
                          className="bg-white border border-oar-border rounded-lg p-4 hover:border-oar-red transition-all text-left"
                        >
                          <FileText className="w-6 h-6 text-oar-red mb-2" />
                          <p className="font-medium text-oar-text-primary">Ressources</p>
                          <p className="text-xs text-oar-text-muted">Logos, certificats</p>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Analytics Section */}
                  {activeSection === 'analytics' && (
                    <div className="space-y-6">
                      <h2 className="font-heading text-xl text-oar-text-primary flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-oar-blue" />
                        Statistiques des Visites
                      </h2>

                      {/* Summary Stats */}
                      {visitStats && (
                        <>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="bg-white border border-oar-border rounded-lg p-4">
                              <div className="flex items-center gap-2 mb-2">
                                <Activity className="w-5 h-5 text-oar-blue" />
                                <span className="text-sm text-oar-text-muted">Aujourd'hui</span>
                              </div>
                              <p className="text-3xl font-bold text-oar-blue">{visitStats.visits_today}</p>
                            </div>
                            <div className="bg-white border border-oar-border rounded-lg p-4">
                              <div className="flex items-center gap-2 mb-2">
                                <Calendar className="w-5 h-5 text-oar-green" />
                                <span className="text-sm text-oar-text-muted">Ce mois</span>
                              </div>
                              <p className="text-3xl font-bold text-oar-green">{visitStats.visits_month}</p>
                            </div>
                            <div className="bg-white border border-oar-border rounded-lg p-4">
                              <div className="flex items-center gap-2 mb-2">
                                <BarChart3 className="w-5 h-5 text-oar-orange" />
                                <span className="text-sm text-oar-text-muted">Cette année</span>
                              </div>
                              <p className="text-3xl font-bold text-oar-orange">{visitStats.visits_year}</p>
                            </div>
                            <div className="bg-white border border-oar-border rounded-lg p-4">
                              <div className="flex items-center gap-2 mb-2">
                                <Users className="w-5 h-5 text-purple-600" />
                                <span className="text-sm text-oar-text-muted">Visiteurs uniques</span>
                              </div>
                              <p className="text-3xl font-bold text-purple-600">{visitStats.unique_visitors}</p>
                            </div>
                          </div>

                          {/* Device, Browser, OS breakdown */}
                          <div className="grid md:grid-cols-3 gap-6">
                            {/* By Device */}
                            <div className="bg-white border border-oar-border rounded-lg p-4">
                              <h3 className="font-medium text-oar-text-primary mb-4 flex items-center gap-2">
                                <Monitor className="w-4 h-4" />
                                Appareils
                              </h3>
                              <div className="space-y-3">
                                {visitStats.by_device?.map((item, idx) => (
                                  <div key={idx} className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      {item.device === 'mobile' ? (
                                        <Smartphone className="w-4 h-4 text-oar-blue" />
                                      ) : item.device === 'tablet' ? (
                                        <Tablet className="w-4 h-4 text-oar-orange" />
                                      ) : (
                                        <Monitor className="w-4 h-4 text-oar-green" />
                                      )}
                                      <span className="text-sm capitalize">{item.device}</span>
                                    </div>
                                    <span className="font-bold text-oar-text-primary">{item.count}</span>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* By Browser */}
                            <div className="bg-white border border-oar-border rounded-lg p-4">
                              <h3 className="font-medium text-oar-text-primary mb-4 flex items-center gap-2">
                                <Globe className="w-4 h-4" />
                                Navigateurs
                              </h3>
                              <div className="space-y-2">
                                {visitStats.by_browser?.map((item, idx) => (
                                  <div key={idx} className="flex items-center justify-between">
                                    <span className="text-sm">{item.browser}</span>
                                    <span className="font-bold text-oar-text-primary">{item.count}</span>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* By OS */}
                            <div className="bg-white border border-oar-border rounded-lg p-4">
                              <h3 className="font-medium text-oar-text-primary mb-4 flex items-center gap-2">
                                <Monitor className="w-4 h-4" />
                                Systèmes d'exploitation
                              </h3>
                              <div className="space-y-2">
                                {visitStats.by_os?.map((item, idx) => (
                                  <div key={idx} className="flex items-center justify-between">
                                    <span className="text-sm">{item.os}</span>
                                    <span className="font-bold text-oar-text-primary">{item.count}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* Visits by Country and City */}
                          <div className="grid md:grid-cols-2 gap-6">
                            {/* By Country */}
                            <div className="bg-white border border-oar-border rounded-lg p-4">
                              <h3 className="font-medium text-oar-text-primary mb-4 flex items-center gap-2">
                                <Globe className="w-4 h-4 text-oar-blue" />
                                Visites par Pays
                              </h3>
                              <div className="space-y-2 max-h-64 overflow-y-auto">
                                {visitStats.by_country?.map((item, idx) => (
                                  <div key={idx} className="flex items-center justify-between py-1 border-b border-oar-border last:border-0">
                                    <span className="text-sm">{item.country}</span>
                                    <span className="font-bold text-oar-blue">{item.count}</span>
                                  </div>
                                ))}
                                {visitStats.by_country?.length === 0 && (
                                  <p className="text-sm text-oar-text-muted">Aucune donnée</p>
                                )}
                              </div>
                            </div>

                            {/* By City */}
                            <div className="bg-white border border-oar-border rounded-lg p-4">
                              <h3 className="font-medium text-oar-text-primary mb-4 flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-oar-orange" />
                                Visites par Ville
                              </h3>
                              <div className="space-y-2 max-h-64 overflow-y-auto">
                                {visitStats.by_city?.map((item, idx) => (
                                  <div key={idx} className="flex items-center justify-between py-1 border-b border-oar-border last:border-0">
                                    <span className="text-sm">{item.city}, {item.country}</span>
                                    <span className="font-bold text-oar-orange">{item.count}</span>
                                  </div>
                                ))}
                                {visitStats.by_city?.length === 0 && (
                                  <p className="text-sm text-oar-text-muted">Aucune donnée</p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Pages visited */}
                          <div className="bg-white border border-oar-border rounded-lg p-4">
                            <h3 className="font-medium text-oar-text-primary mb-4 flex items-center gap-2">
                              <FileText className="w-4 h-4 text-oar-green" />
                              Pages les plus visitées
                            </h3>
                            <div className="grid md:grid-cols-2 gap-2">
                              {visitStats.by_page?.map((item, idx) => (
                                <div key={idx} className="flex items-center justify-between py-2 px-3 bg-oar-bg-secondary rounded">
                                  <span className="text-sm font-mono">{item.page}</span>
                                  <span className="font-bold text-oar-green">{item.count}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </>
                      )}

                      {/* Recent Visits Table */}
                      <div className="bg-white border border-oar-border rounded-lg overflow-hidden">
                        <div className="px-4 py-3 bg-oar-bg-secondary border-b border-oar-border">
                          <h3 className="font-heading text-lg text-oar-text-primary flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            Visites récentes ({recentVisits.length})
                          </h3>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-oar-bg-secondary">
                              <tr>
                                <th className="text-left px-4 py-3 text-xs text-oar-text-muted uppercase">Date/Heure</th>
                                <th className="text-left px-4 py-3 text-xs text-oar-text-muted uppercase">IP</th>
                                <th className="text-left px-4 py-3 text-xs text-oar-text-muted uppercase">Pays/Ville</th>
                                <th className="text-left px-4 py-3 text-xs text-oar-text-muted uppercase">Appareil</th>
                                <th className="text-left px-4 py-3 text-xs text-oar-text-muted uppercase">Navigateur</th>
                                <th className="text-left px-4 py-3 text-xs text-oar-text-muted uppercase">Page</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-oar-border">
                              {recentVisits.map((visit) => (
                                <tr key={visit.id} className="hover:bg-oar-bg-secondary/50">
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <div className="text-sm text-oar-text-primary">
                                      {visit.visited_at ? new Date(visit.visited_at).toLocaleDateString('fr-FR') : '-'}
                                    </div>
                                    <div className="text-xs text-oar-text-muted">
                                      {visit.visited_at ? new Date(visit.visited_at).toLocaleTimeString('fr-FR') : '-'}
                                    </div>
                                  </td>
                                  <td className="px-4 py-3">
                                    <code className="text-xs font-mono bg-oar-bg-secondary px-2 py-1 rounded">
                                      {visit.ip_address}
                                    </code>
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="text-sm font-medium text-oar-text-primary">{visit.country}</div>
                                    <div className="text-xs text-oar-text-muted">{visit.city}</div>
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex items-center gap-1">
                                      {visit.device_type === 'mobile' ? (
                                        <Smartphone className="w-4 h-4 text-oar-blue" />
                                      ) : visit.device_type === 'tablet' ? (
                                        <Tablet className="w-4 h-4 text-oar-orange" />
                                      ) : (
                                        <Monitor className="w-4 h-4 text-oar-green" />
                                      )}
                                      <span className="text-sm capitalize">{visit.device_type}</span>
                                    </div>
                                    <div className="text-xs text-oar-text-muted">{visit.os}</div>
                                  </td>
                                  <td className="px-4 py-3 text-sm text-oar-text-secondary">
                                    {visit.browser}
                                  </td>
                                  <td className="px-4 py-3">
                                    <span className="text-xs font-mono bg-oar-blue-soft text-oar-blue px-2 py-1 rounded">
                                      {visit.page_visited}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {recentVisits.length === 0 && (
                          <div className="text-center py-8 text-oar-text-muted">
                            <Activity className="w-12 h-12 mx-auto mb-2 text-oar-text-muted" />
                            Aucune visite enregistrée
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Members Section */}
                  {activeSection === 'members' && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h2 className="font-heading text-xl text-oar-text-primary">
                          Membres inscrits ({members.length})
                        </h2>
                        <div className="flex items-center gap-2">
                          <a
                            href={`${API}/admin/export/members?format=excel`}
                            className="flex items-center gap-2 bg-oar-green hover:bg-oar-green-dark text-white px-3 py-1.5 rounded-lg text-sm"
                          >
                            <FileSpreadsheet className="w-4 h-4" />
                            Excel
                          </a>
                          <a
                            href={`${API}/admin/export/members?format=pdf`}
                            className="flex items-center gap-2 bg-oar-red hover:bg-oar-red-dark text-white px-3 py-1.5 rounded-lg text-sm"
                          >
                            <FileText className="w-4 h-4" />
                            PDF
                          </a>
                        </div>
                      </div>
                      
                      <div className="bg-white border border-oar-border rounded-lg overflow-hidden">
                        <table className="w-full">
                          <thead className="bg-oar-bg-secondary">
                            <tr>
                              <th className="text-left px-4 py-3 text-xs text-oar-text-muted uppercase">Membre</th>
                              <th className="text-left px-4 py-3 text-xs text-oar-text-muted uppercase">Téléphone</th>
                              <th className="text-left px-4 py-3 text-xs text-oar-text-muted uppercase">Matricule</th>
                              <th className="text-left px-4 py-3 text-xs text-oar-text-muted uppercase">Type</th>
                              <th className="text-left px-4 py-3 text-xs text-oar-text-muted uppercase">Pays</th>
                              <th className="text-left px-4 py-3 text-xs text-oar-text-muted uppercase">Statut</th>
                              <th className="text-right px-4 py-3 text-xs text-oar-text-muted uppercase">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-oar-border">
                            {members.map((member) => (
                              <tr key={member.id} className="hover:bg-oar-bg-secondary/50">
                                <td className="px-4 py-3">
                                  <div>
                                    <p className="font-medium text-oar-text-primary">
                                      {member.first_name} {member.last_name}
                                    </p>
                                    <p className="text-xs text-oar-text-muted">{member.email}</p>
                                    <p className="text-xs text-oar-blue">{member.member_number}</p>
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  {member.phone ? (
                                    <a href={`tel:${member.phone}`} className="flex items-center gap-1 text-sm text-oar-text-secondary hover:text-oar-blue">
                                      <Phone className="w-3 h-3" />
                                      {member.phone}
                                    </a>
                                  ) : (
                                    <span className="text-xs text-oar-text-muted">-</span>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  <span className="px-2 py-1 bg-oar-orange-soft text-oar-orange rounded font-mono text-sm font-bold">
                                    {member.matricule || '-'}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`px-2 py-1 rounded-full text-xs ${
                                    member.membership_type === 'premium'
                                      ? 'bg-oar-red-soft text-oar-red'
                                      : 'bg-oar-blue-soft text-oar-blue'
                                  }`}>
                                    {member.membership_type}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-oar-text-secondary">
                                  {member.city ? `${member.city}, ` : ''}{member.country}
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`flex items-center gap-1 text-xs ${
                                    member.is_active ? 'text-oar-green' : 'text-oar-red'
                                  }`}>
                                    {member.is_active ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                                    {member.is_active ? 'Actif' : 'Inactif'}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center justify-end gap-1">
                                    {member.is_active && (
                                      <>
                                        <button
                                          onClick={() => { setPreviewMember(member); setPreviewType('certificate'); }}
                                          className="p-1.5 text-oar-text-muted hover:text-oar-blue hover:bg-oar-blue-soft rounded"
                                          title="Aperçu certificat/carte"
                                        >
                                          <ZoomIn className="w-4 h-4" />
                                        </button>
                                        <button
                                          onClick={() => window.open(`${API}/members/${member.id}/certificate`, '_blank')}
                                          className="p-1.5 text-oar-text-muted hover:text-oar-green hover:bg-oar-green-soft rounded"
                                          title="Télécharger certificat PDF"
                                        >
                                          <FileText className="w-4 h-4" />
                                        </button>
                                        <button
                                          onClick={() => window.open(`${API}/members/${member.id}/card`, '_blank')}
                                          className="p-1.5 text-oar-text-muted hover:text-oar-blue hover:bg-oar-blue-soft rounded"
                                          title="Télécharger carte de membre"
                                        >
                                          <CreditCard className="w-4 h-4" />
                                        </button>
                                      </>
                                    )}
                                    <button
                                      onClick={() => window.open(`${API}/members/${member.id}/qrcode`, '_blank')}
                                      className="p-1.5 text-oar-text-muted hover:text-oar-orange hover:bg-oar-orange-soft rounded"
                                      title="QR Code"
                                    >
                                      <QrCode className="w-4 h-4" />
                                    </button>
                                    {member.is_active ? (
                                      <button
                                        onClick={() => deactivateMember(member.id)}
                                        disabled={processingId === member.id}
                                        className="p-1.5 text-oar-text-muted hover:text-oar-orange hover:bg-oar-orange-soft rounded"
                                        title="Désactiver"
                                      >
                                        <UserX className="w-4 h-4" />
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => activateMember(member.id)}
                                        disabled={processingId === member.id}
                                        className="p-1.5 text-oar-text-muted hover:text-oar-green hover:bg-oar-green-soft rounded"
                                        title="Activer"
                                      >
                                        <UserCheck className="w-4 h-4" />
                                      </button>
                                    )}
                                    <button
                                      onClick={() => deleteMember(member.id)}
                                      className="p-1.5 text-oar-text-muted hover:text-oar-red hover:bg-oar-red-soft rounded"
                                      title="Supprimer"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {members.length === 0 && (
                          <div className="text-center py-8 text-oar-text-muted">Aucun membre inscrit</div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Applications Section */}
                  {activeSection === 'applications' && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h2 className="font-heading text-xl text-oar-text-primary">
                          Candidatures étudiants ({applications.length})
                        </h2>
                        <div className="flex items-center gap-2">
                          <a
                            href={`${API}/admin/export/applications?format=excel`}
                            className="flex items-center gap-2 bg-oar-green hover:bg-oar-green-dark text-white px-3 py-1.5 rounded-lg text-sm"
                          >
                            <FileSpreadsheet className="w-4 h-4" />
                            Excel
                          </a>
                          <a
                            href={`${API}/admin/export/applications?format=pdf`}
                            className="flex items-center gap-2 bg-oar-red hover:bg-oar-red-dark text-white px-3 py-1.5 rounded-lg text-sm"
                          >
                            <FileText className="w-4 h-4" />
                            PDF
                          </a>
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        {applications.map((app) => (
                          <div key={app.id} className="bg-white border border-oar-border rounded-lg p-4">
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <p className="font-medium text-oar-text-primary">
                                    {app.first_name} {app.last_name}
                                  </p>
                                  <span className="px-2 py-0.5 bg-oar-blue-soft text-oar-blue text-xs rounded-full">
                                    {app.application_number}
                                  </span>
                                </div>
                                <p className="text-sm text-oar-text-muted">{app.email}</p>
                                {app.phone && (
                                  <a href={`tel:${app.phone}`} className="flex items-center gap-1 text-sm text-oar-blue hover:text-oar-blue-dark">
                                    <Phone className="w-3 h-3" />
                                    {app.phone}
                                  </a>
                                )}
                                <p className="text-sm text-oar-text-secondary">{app.country} • {app.field_of_study}</p>
                                <p className="text-xs text-oar-text-muted mt-1">{formatDate(app.created_at)}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  app.status === 'approved' ? 'bg-oar-green-soft text-oar-green' :
                                  app.status === 'rejected' ? 'bg-oar-red-soft text-oar-red' :
                                  app.status === 'reviewing' ? 'bg-oar-orange-soft text-oar-orange' :
                                  'bg-oar-bg-secondary text-oar-text-muted'
                                }`}>
                                  {app.status}
                                </span>
                              </div>
                            </div>
                            {app.motivation && (
                              <div className="mt-3 p-3 bg-oar-bg-secondary rounded text-sm text-oar-text-secondary">
                                <p className="text-xs text-oar-text-muted mb-1">Motivation:</p>
                                {app.motivation}
                              </div>
                            )}
                            <div className="mt-3 flex gap-2">
                              <button
                                onClick={() => updateApplicationStatus(app.id, 'approved')}
                                className="px-3 py-1 bg-oar-green text-white text-xs rounded hover:bg-oar-green-dark"
                              >
                                Approuver
                              </button>
                              <button
                                onClick={() => updateApplicationStatus(app.id, 'reviewing')}
                                className="px-3 py-1 bg-oar-orange text-white text-xs rounded hover:bg-oar-orange-dark"
                              >
                                En revue
                              </button>
                              <button
                                onClick={() => updateApplicationStatus(app.id, 'rejected')}
                                className="px-3 py-1 bg-oar-red text-white text-xs rounded hover:bg-oar-red-dark"
                              >
                                Rejeter
                              </button>
                            </div>
                          </div>
                        ))}
                        {applications.length === 0 && (
                          <div className="text-center py-8 text-oar-text-muted bg-white rounded-lg border border-oar-border">
                            Aucune candidature
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Contacts Section */}
                  {activeSection === 'contacts' && (
                    <div className="space-y-4">
                      <h2 className="font-heading text-xl text-oar-text-primary">
                        Messages de contact ({contacts.length})
                      </h2>
                      
                      <div className="space-y-4">
                        {contacts.map((contact) => (
                          <div key={contact.id} className={`bg-white border rounded-lg p-4 ${
                            contact.is_read ? 'border-oar-border' : 'border-oar-orange'
                          }`}>
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <p className="font-medium text-oar-text-primary">{contact.name}</p>
                                  {!contact.is_read && (
                                    <span className="px-2 py-0.5 bg-oar-orange text-white text-xs rounded-full">Nouveau</span>
                                  )}
                                </div>
                                <p className="text-sm text-oar-text-muted">{contact.email}</p>
                                <p className="text-sm font-medium text-oar-blue mt-1">{contact.subject}</p>
                              </div>
                              <p className="text-xs text-oar-text-muted">{formatDate(contact.received_at)}</p>
                            </div>
                            <div className="mt-3 p-3 bg-oar-bg-secondary rounded text-sm text-oar-text-secondary">
                              {contact.message}
                            </div>
                            <div className="mt-3 flex gap-2">
                              <button
                                onClick={() => openReplyModal(contact)}
                                className="px-3 py-1 bg-oar-blue text-white text-xs rounded hover:bg-oar-blue-dark flex items-center gap-1"
                                data-testid={`reply-contact-${contact.id}`}
                              >
                                <Send className="w-3 h-3" />
                                Répondre
                              </button>
                              {!contact.is_read && (
                                <button
                                  onClick={() => markContactRead(contact.id)}
                                  className="px-3 py-1 bg-oar-green text-white text-xs rounded hover:bg-oar-green-dark"
                                >
                                  Marquer comme lu
                                </button>
                              )}
                              <button
                                onClick={() => deleteContact(contact.id)}
                                className="px-3 py-1 bg-oar-red text-white text-xs rounded hover:bg-oar-red-dark"
                              >
                                Supprimer
                              </button>
                            </div>
                          </div>
                        ))}
                        {contacts.length === 0 && (
                          <div className="text-center py-8 text-oar-text-muted bg-white rounded-lg border border-oar-border">
                            Aucun message
                          </div>
                        )}
                      </div>

                      {/* Reply Modal */}
                      {replyModal.open && replyModal.contact && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b border-oar-border">
                              <div className="flex items-center justify-between">
                                <h3 className="font-heading text-lg text-oar-text-primary flex items-center gap-2">
                                  <Send className="w-5 h-5 text-oar-blue" />
                                  Répondre au message
                                </h3>
                                <button
                                  onClick={closeReplyModal}
                                  className="text-oar-text-muted hover:text-oar-text-primary"
                                >
                                  <X className="w-5 h-5" />
                                </button>
                              </div>
                            </div>
                            
                            <div className="p-6 space-y-4">
                              {/* Original Message */}
                              <div className="bg-oar-bg-secondary rounded-lg p-4">
                                <p className="text-xs text-oar-text-muted mb-1">Message original de</p>
                                <p className="font-medium text-oar-text-primary">{replyModal.contact.name}</p>
                                <p className="text-sm text-oar-blue">{replyModal.contact.email}</p>
                                <p className="text-sm font-medium mt-2">{replyModal.contact.subject}</p>
                                <div className="mt-2 text-sm text-oar-text-secondary border-l-2 border-oar-orange pl-3">
                                  {replyModal.contact.message}
                                </div>
                              </div>

                              {/* Reply Form */}
                              <div>
                                <label className="block text-sm text-oar-text-secondary mb-1">
                                  Destinataire
                                </label>
                                <input
                                  type="text"
                                  value={replyModal.contact.email}
                                  disabled
                                  className="w-full border border-oar-border rounded-lg px-4 py-2 bg-oar-bg-secondary text-oar-text-muted"
                                />
                              </div>

                              <div>
                                <label className="block text-sm text-oar-text-secondary mb-1">
                                  Sujet *
                                </label>
                                <input
                                  type="text"
                                  value={replySubject}
                                  onChange={(e) => setReplySubject(e.target.value)}
                                  className="w-full border border-oar-border rounded-lg px-4 py-2 focus:border-oar-blue focus:outline-none"
                                  data-testid="reply-subject-input"
                                />
                              </div>

                              <div>
                                <label className="block text-sm text-oar-text-secondary mb-1">
                                  Votre réponse *
                                </label>
                                <RichTextEditor
                                  value={replyMessage}
                                  onChange={setReplyMessage}
                                  placeholder="Écrivez votre réponse ici..."
                                />
                                <p className="text-xs text-oar-text-muted mt-1">
                                  Utilisez la barre d'outils pour formater votre texte (gras, italique, couleur, etc.)
                                </p>
                              </div>

                              <div className="flex justify-end gap-3 pt-4">
                                <button
                                  onClick={closeReplyModal}
                                  className="px-4 py-2 border border-oar-border rounded-lg text-oar-text-secondary hover:bg-oar-bg-secondary transition-all"
                                >
                                  Annuler
                                </button>
                                <button
                                  onClick={sendContactReply}
                                  disabled={sendingReply}
                                  className="flex items-center gap-2 bg-oar-blue hover:bg-oar-blue-dark disabled:bg-gray-400 text-white px-6 py-2 rounded-lg transition-all"
                                  data-testid="send-reply-btn"
                                >
                                  {sendingReply ? (
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Send className="w-4 h-4" />
                                  )}
                                  {sendingReply ? 'Envoi en cours...' : 'Envoyer la réponse'}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Newsletter Section */}
                  {activeSection === 'newsletter' && (
                    <div className="space-y-6">
                      {/* Mass Email Form */}
                      <div className="bg-white border border-oar-border rounded-lg p-6">
                        <h2 className="font-heading text-xl text-oar-text-primary mb-4 flex items-center gap-2">
                          <Send className="w-5 h-5 text-oar-blue" />
                          Envoi d'emails en masse
                        </h2>
                        
                        {/* Target Selection */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                          <button
                            onClick={() => setEmailTarget('newsletter')}
                            className={`p-3 rounded-lg border-2 transition-all ${
                              emailTarget === 'newsletter'
                                ? 'border-oar-blue bg-oar-blue-soft'
                                : 'border-oar-border hover:border-oar-blue/50'
                            }`}
                          >
                            <Mail className={`w-5 h-5 mx-auto mb-1 ${emailTarget === 'newsletter' ? 'text-oar-blue' : 'text-oar-text-muted'}`} />
                            <p className={`text-sm font-medium ${emailTarget === 'newsletter' ? 'text-oar-blue' : 'text-oar-text-secondary'}`}>
                              Newsletter
                            </p>
                            <p className="text-xs text-oar-text-muted">{recipientsCounts.newsletter || 0} abonnés</p>
                          </button>
                          
                          <button
                            onClick={() => setEmailTarget('premium')}
                            className={`p-3 rounded-lg border-2 transition-all ${
                              emailTarget === 'premium'
                                ? 'border-oar-red bg-oar-red-soft'
                                : 'border-oar-border hover:border-oar-red/50'
                            }`}
                          >
                            <CreditCard className={`w-5 h-5 mx-auto mb-1 ${emailTarget === 'premium' ? 'text-oar-red' : 'text-oar-text-muted'}`} />
                            <p className={`text-sm font-medium ${emailTarget === 'premium' ? 'text-oar-red' : 'text-oar-text-secondary'}`}>
                              Premium
                            </p>
                            <p className="text-xs text-oar-text-muted">{recipientsCounts.premium || 0} membres</p>
                          </button>
                          
                          <button
                            onClick={() => setEmailTarget('standard')}
                            className={`p-3 rounded-lg border-2 transition-all ${
                              emailTarget === 'standard'
                                ? 'border-oar-green bg-oar-green-soft'
                                : 'border-oar-border hover:border-oar-green/50'
                            }`}
                          >
                            <Users className={`w-5 h-5 mx-auto mb-1 ${emailTarget === 'standard' ? 'text-oar-green' : 'text-oar-text-muted'}`} />
                            <p className={`text-sm font-medium ${emailTarget === 'standard' ? 'text-oar-green' : 'text-oar-text-secondary'}`}>
                              Standard
                            </p>
                            <p className="text-xs text-oar-text-muted">{recipientsCounts.standard || 0} membres</p>
                          </button>
                          
                          <button
                            onClick={() => setEmailTarget('all_members')}
                            className={`p-3 rounded-lg border-2 transition-all ${
                              emailTarget === 'all_members'
                                ? 'border-oar-orange bg-oar-orange-soft'
                                : 'border-oar-border hover:border-oar-orange/50'
                            }`}
                          >
                            <Users className={`w-5 h-5 mx-auto mb-1 ${emailTarget === 'all_members' ? 'text-oar-orange' : 'text-oar-text-muted'}`} />
                            <p className={`text-sm font-medium ${emailTarget === 'all_members' ? 'text-oar-orange' : 'text-oar-text-secondary'}`}>
                              Tous membres
                            </p>
                            <p className="text-xs text-oar-text-muted">{recipientsCounts.all_members || 0} membres</p>
                          </button>
                        </div>
                        
                        {/* Email Form */}
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-oar-text-secondary mb-1">
                              Sujet de l'email
                            </label>
                            <input
                              type="text"
                              value={emailSubject}
                              onChange={(e) => setEmailSubject(e.target.value)}
                              placeholder="Entrez le sujet de votre email..."
                              className="w-full px-4 py-2 border border-oar-border rounded-lg focus:ring-2 focus:ring-oar-blue focus:border-oar-blue"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-oar-text-secondary mb-1">
                              Message
                            </label>
                            <textarea
                              value={emailMessage}
                              onChange={(e) => setEmailMessage(e.target.value)}
                              placeholder="Écrivez votre message ici..."
                              rows={6}
                              className="w-full px-4 py-2 border border-oar-border rounded-lg focus:ring-2 focus:ring-oar-blue focus:border-oar-blue resize-none"
                            />
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <p className="text-sm text-oar-text-muted">
                              Cet email sera envoyé à{' '}
                              <span className="font-bold text-oar-blue">
                                {emailTarget === 'newsletter' && recipientsCounts.newsletter}
                                {emailTarget === 'premium' && recipientsCounts.premium}
                                {emailTarget === 'standard' && recipientsCounts.standard}
                                {emailTarget === 'all_members' && recipientsCounts.all_members}
                              </span>{' '}
                              destinataires
                            </p>
                            <button
                              onClick={sendMassEmail}
                              disabled={sendingEmail || !emailSubject.trim() || !emailMessage.trim()}
                              className="flex items-center gap-2 bg-oar-blue hover:bg-oar-blue-dark disabled:bg-gray-400 text-white px-6 py-2 rounded-lg font-medium transition-all"
                            >
                              {sendingEmail ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <Send className="w-4 h-4" />
                              )}
                              {sendingEmail ? 'Envoi...' : 'Envoyer'}
                            </button>
                          </div>
                        </div>
                        
                        <div className={`mt-4 p-3 rounded-lg ${
                          recipientsCounts.onesignal_configured 
                            ? 'bg-green-50 border border-green-200' 
                            : 'bg-oar-orange-soft border border-oar-orange/30'
                        }`}>
                          <p className={`text-sm flex items-center gap-2 ${
                            recipientsCounts.onesignal_configured ? 'text-green-700' : 'text-oar-orange'
                          }`}>
                            {recipientsCounts.onesignal_configured ? (
                              <>
                                <CheckCircle className="w-4 h-4" />
                                <span><strong>OneSignal activé !</strong> Les emails seront envoyés automatiquement aux destinataires.</span>
                              </>
                            ) : (
                              <>
                                <AlertCircle className="w-4 h-4" />
                                <span><strong>Note :</strong> Les emails sont actuellement stockés en base de données (simulés). L'envoi réel nécessite l'intégration OneSignal.</span>
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                      
                      {/* Newsletter Subscribers List */}
                      <div className="bg-white border border-oar-border rounded-lg overflow-hidden">
                        <div className="px-4 py-3 bg-oar-bg-secondary border-b border-oar-border">
                          <h3 className="font-heading text-lg text-oar-text-primary">
                            Abonnés Newsletter ({newsletter.length})
                          </h3>
                        </div>
                        <table className="w-full">
                          <thead className="bg-oar-bg-secondary">
                            <tr>
                              <th className="text-left px-4 py-3 text-xs text-oar-text-muted uppercase">Email</th>
                              <th className="text-left px-4 py-3 text-xs text-oar-text-muted uppercase">Langue</th>
                              <th className="text-left px-4 py-3 text-xs text-oar-text-muted uppercase">Date</th>
                              <th className="text-right px-4 py-3 text-xs text-oar-text-muted uppercase">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-oar-border">
                            {newsletter.map((sub) => (
                              <tr key={sub.id} className="hover:bg-oar-bg-secondary/50">
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => copyToClipboard(sub.email)}
                                      className="text-oar-text-primary hover:text-oar-blue"
                                    >
                                      {sub.email}
                                    </button>
                                    {copiedText === sub.email && (
                                      <Check className="w-4 h-4 text-oar-green" />
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-sm text-oar-text-secondary">{sub.language?.toUpperCase()}</td>
                                <td className="px-4 py-3 text-xs text-oar-text-muted">{formatDate(sub.subscribed_at)}</td>
                                <td className="px-4 py-3 text-right">
                                  <button
                                    onClick={() => deleteSubscriber(sub.id)}
                                    className="p-1 text-oar-text-muted hover:text-oar-red"
                                    title="Supprimer"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {newsletter.length === 0 && (
                          <div className="text-center py-8 text-oar-text-muted">Aucun abonné</div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Publications Section */}
                  {activeSection === 'publications' && (
                    <div className="space-y-6">
                      {/* Publication Form Modal */}
                      {showPublicationForm && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="sticky top-0 bg-white border-b border-oar-border px-6 py-4 flex items-center justify-between">
                              <h3 className="font-heading text-xl text-oar-text-primary">
                                {editingPublication ? 'Modifier la publication' : 'Nouvelle publication'}
                              </h3>
                              <button
                                onClick={() => { setShowPublicationForm(false); resetPublicationForm(); }}
                                className="p-2 hover:bg-oar-bg-secondary rounded"
                              >
                                <X className="w-5 h-5" />
                              </button>
                            </div>
                            
                            <div className="p-6 space-y-6">
                              {/* Type & Category */}
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-oar-text-secondary mb-1">
                                    Type de publication
                                  </label>
                                  <select
                                    value={publicationForm.publication_type}
                                    onChange={(e) => setPublicationForm(prev => ({ ...prev, publication_type: e.target.value }))}
                                    className="w-full px-3 py-2 border border-oar-border rounded-lg focus:border-oar-blue focus:ring-1 focus:ring-oar-blue"
                                  >
                                    <option value="article">Article</option>
                                    <option value="annonce">Annonce</option>
                                    <option value="video">Vidéo</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-oar-text-secondary mb-1">
                                    Catégorie
                                  </label>
                                  <select
                                    value={publicationForm.category}
                                    onChange={(e) => setPublicationForm(prev => ({ ...prev, category: e.target.value }))}
                                    className="w-full px-3 py-2 border border-oar-border rounded-lg focus:border-oar-blue focus:ring-1 focus:ring-oar-blue"
                                  >
                                    <option value="article">Article</option>
                                    <option value="annonce">Annonce</option>
                                    <option value="rapport">Rapport Annuel</option>
                                    <option value="note_strategique">Note Stratégique</option>
                                    <option value="analyse_geopolitique">Analyse Géopolitique</option>
                                  </select>
                                </div>
                              </div>

                              {/* Titles */}
                              <div className="space-y-3">
                                <h4 className="font-medium text-oar-text-primary flex items-center gap-2">
                                  <Globe className="w-4 h-4" /> Titres (multilingue)
                                </h4>
                                <input
                                  type="text"
                                  placeholder="Titre (Français) *"
                                  value={publicationForm.title_fr}
                                  onChange={(e) => setPublicationForm(prev => ({ ...prev, title_fr: e.target.value }))}
                                  className="w-full px-3 py-2 border border-oar-border rounded-lg focus:border-oar-blue focus:ring-1 focus:ring-oar-blue"
                                />
                                <input
                                  type="text"
                                  placeholder="Title (English)"
                                  value={publicationForm.title_en}
                                  onChange={(e) => setPublicationForm(prev => ({ ...prev, title_en: e.target.value }))}
                                  className="w-full px-3 py-2 border border-oar-border rounded-lg focus:border-oar-blue focus:ring-1 focus:ring-oar-blue"
                                />
                                <input
                                  type="text"
                                  placeholder="Заголовок (Русский)"
                                  value={publicationForm.title_ru}
                                  onChange={(e) => setPublicationForm(prev => ({ ...prev, title_ru: e.target.value }))}
                                  className="w-full px-3 py-2 border border-oar-border rounded-lg focus:border-oar-blue focus:ring-1 focus:ring-oar-blue"
                                />
                              </div>

                              {/* Author & Slug */}
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-oar-text-secondary mb-1">
                                    Auteur *
                                  </label>
                                  <input
                                    type="text"
                                    placeholder="Ex: Comité Scientifique OAR"
                                    value={publicationForm.author}
                                    onChange={(e) => setPublicationForm(prev => ({ ...prev, author: e.target.value }))}
                                    className="w-full px-3 py-2 border border-oar-border rounded-lg focus:border-oar-blue focus:ring-1 focus:ring-oar-blue"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-oar-text-secondary mb-1">
                                    Slug URL (auto-généré si vide)
                                  </label>
                                  <input
                                    type="text"
                                    placeholder="mon-article-slug"
                                    value={publicationForm.slug}
                                    onChange={(e) => setPublicationForm(prev => ({ ...prev, slug: e.target.value }))}
                                    className="w-full px-3 py-2 border border-oar-border rounded-lg focus:border-oar-blue focus:ring-1 focus:ring-oar-blue"
                                  />
                                </div>
                              </div>

                              {/* Excerpts */}
                              <div className="space-y-3">
                                <h4 className="font-medium text-oar-text-primary">Résumés</h4>
                                <textarea
                                  placeholder="Résumé (Français)"
                                  value={publicationForm.excerpt_fr}
                                  onChange={(e) => setPublicationForm(prev => ({ ...prev, excerpt_fr: e.target.value }))}
                                  rows={2}
                                  className="w-full px-3 py-2 border border-oar-border rounded-lg focus:border-oar-blue focus:ring-1 focus:ring-oar-blue"
                                />
                                <textarea
                                  placeholder="Excerpt (English)"
                                  value={publicationForm.excerpt_en}
                                  onChange={(e) => setPublicationForm(prev => ({ ...prev, excerpt_en: e.target.value }))}
                                  rows={2}
                                  className="w-full px-3 py-2 border border-oar-border rounded-lg focus:border-oar-blue focus:ring-1 focus:ring-oar-blue"
                                />
                              </div>

                              {/* Content */}
                              <div className="space-y-3">
                                <h4 className="font-medium text-oar-text-primary">Contenu complet</h4>
                                <textarea
                                  placeholder="Contenu (Français)"
                                  value={publicationForm.content_fr}
                                  onChange={(e) => setPublicationForm(prev => ({ ...prev, content_fr: e.target.value }))}
                                  rows={5}
                                  className="w-full px-3 py-2 border border-oar-border rounded-lg focus:border-oar-blue focus:ring-1 focus:ring-oar-blue"
                                />
                              </div>

                              {/* Media */}
                              <div className="space-y-3">
                                <h4 className="font-medium text-oar-text-primary flex items-center gap-2">
                                  <Image className="w-4 h-4" /> Médias
                                </h4>
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="block text-sm text-oar-text-secondary mb-1">
                                      Image de couverture (URL)
                                    </label>
                                    <input
                                      type="url"
                                      placeholder="https://..."
                                      value={publicationForm.image_url}
                                      onChange={(e) => setPublicationForm(prev => ({ ...prev, image_url: e.target.value }))}
                                      className="w-full px-3 py-2 border border-oar-border rounded-lg focus:border-oar-blue focus:ring-1 focus:ring-oar-blue"
                                    />
                                    <p className="text-xs text-oar-text-muted mt-1">
                                      <strong>Dimensions recommandées :</strong> 1200 x 630 pixels (ratio 1.91:1)
                                    </p>
                                    <p className="text-xs text-oar-text-muted">
                                      Format : JPG, PNG ou WebP | Taille max : 2 Mo
                                    </p>
                                  </div>
                                  <div>
                                    <label className="block text-sm text-oar-text-secondary mb-1">
                                      URL Vidéo (YouTube, etc.)
                                    </label>
                                    <input
                                      type="url"
                                      placeholder="https://youtube.com/..."
                                      value={publicationForm.video_url}
                                      onChange={(e) => setPublicationForm(prev => ({ ...prev, video_url: e.target.value }))}
                                      className="w-full px-3 py-2 border border-oar-border rounded-lg focus:border-oar-blue focus:ring-1 focus:ring-oar-blue"
                                    />
                                  </div>
                                </div>
                                {publicationForm.image_url && (
                                  <div className="mt-2">
                                    <img src={publicationForm.image_url} alt="Preview" className="h-32 object-cover rounded-lg" />
                                  </div>
                                )}
                              </div>

                              {/* Attachments */}
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <h4 className="font-medium text-oar-text-primary flex items-center gap-2">
                                    <File className="w-4 h-4" /> Fichiers joints (PDF, images)
                                  </h4>
                                  <button
                                    type="button"
                                    onClick={addAttachment}
                                    className="flex items-center gap-1 text-sm text-oar-blue hover:text-oar-blue-dark"
                                  >
                                    <Plus className="w-4 h-4" /> Ajouter
                                  </button>
                                </div>
                                {publicationForm.attachments.length > 0 ? (
                                  <div className="space-y-2">
                                    {publicationForm.attachments.map((att, index) => (
                                      <div key={index} className="flex items-center justify-between bg-oar-bg-secondary p-3 rounded-lg">
                                        <div className="flex items-center gap-2">
                                          {att.type === 'pdf' ? (
                                            <FileText className="w-4 h-4 text-red-500" />
                                          ) : att.type === 'image' ? (
                                            <Image className="w-4 h-4 text-green-500" />
                                          ) : (
                                            <File className="w-4 h-4 text-blue-500" />
                                          )}
                                          <span className="text-sm">{att.name}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <a href={att.url} target="_blank" rel="noopener noreferrer" className="text-oar-blue hover:text-oar-blue-dark">
                                            <ExternalLink className="w-4 h-4" />
                                          </a>
                                          <button onClick={() => removeAttachment(index)} className="text-oar-red hover:text-oar-red-dark">
                                            <Trash2 className="w-4 h-4" />
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-oar-text-muted">Aucun fichier joint</p>
                                )}
                              </div>

                              {/* Published */}
                              <div className="flex items-center gap-3">
                                <input
                                  type="checkbox"
                                  id="is_published"
                                  checked={publicationForm.is_published}
                                  onChange={(e) => setPublicationForm(prev => ({ ...prev, is_published: e.target.checked }))}
                                  className="w-4 h-4"
                                />
                                <label htmlFor="is_published" className="text-sm text-oar-text-secondary">
                                  Publier immédiatement
                                </label>
                              </div>
                            </div>

                            <div className="sticky bottom-0 bg-white border-t border-oar-border px-6 py-4 flex justify-end gap-3">
                              <button
                                onClick={() => { setShowPublicationForm(false); resetPublicationForm(); }}
                                className="px-4 py-2 text-oar-text-secondary hover:bg-oar-bg-secondary rounded-lg"
                              >
                                Annuler
                              </button>
                              <button
                                onClick={savePublication}
                                disabled={savingPublication}
                                className="flex items-center gap-2 px-6 py-2 bg-oar-blue text-white rounded-lg hover:bg-oar-blue-dark disabled:opacity-50"
                              >
                                {savingPublication ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <CheckCircle className="w-4 h-4" />
                                )}
                                {editingPublication ? 'Mettre à jour' : 'Créer'}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Header */}
                      <div className="flex items-center justify-between">
                        <h2 className="font-heading text-xl text-oar-text-primary">
                          Publications ({publications.length})
                        </h2>
                        <button
                          onClick={() => openPublicationForm()}
                          className="flex items-center gap-2 px-4 py-2 bg-oar-blue text-white rounded-lg hover:bg-oar-blue-dark"
                        >
                          <Plus className="w-4 h-4" />
                          Nouvelle publication
                        </button>
                      </div>

                      {/* Publications List */}
                      <div className="space-y-4">
                        {publications.map((pub) => (
                          <div key={pub.id} className={`bg-white border rounded-lg p-4 ${
                            pub.is_published ? 'border-oar-border' : 'border-oar-orange border-dashed'
                          }`}>
                            <div className="flex items-start justify-between gap-4">
                              {pub.image_url && (
                                <img src={pub.image_url} alt="" className="w-24 h-16 object-cover rounded" />
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`px-2 py-0.5 text-xs rounded ${
                                    pub.publication_type === 'video' ? 'bg-purple-100 text-purple-700' :
                                    pub.publication_type === 'annonce' ? 'bg-orange-100 text-orange-700' :
                                    'bg-blue-100 text-blue-700'
                                  }`}>
                                    {pub.publication_type === 'video' ? 'Vidéo' : pub.publication_type === 'annonce' ? 'Annonce' : 'Article'}
                                  </span>
                                  <span className="text-xs text-oar-text-muted">{pub.category}</span>
                                  {!pub.is_published && (
                                    <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded">Brouillon</span>
                                  )}
                                </div>
                                <h3 className="font-medium text-oar-text-primary truncate">{pub.title_fr}</h3>
                                <p className="text-sm text-oar-text-muted truncate">{pub.excerpt_fr}</p>
                                <div className="flex items-center gap-4 mt-2 text-xs text-oar-text-muted">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {formatDate(pub.published_at)}
                                  </span>
                                  <span>{pub.author}</span>
                                  <span className="flex items-center gap-1">
                                    <Eye className="w-3 h-3" />
                                    {pub.views || 0} vues
                                  </span>
                                  {pub.attachments?.length > 0 && (
                                    <span className="flex items-center gap-1">
                                      <File className="w-3 h-3" />
                                      {pub.attachments.length} fichier(s)
                                    </span>
                                  )}
                                  {pub.video_url && (
                                    <span className="flex items-center gap-1 text-purple-600">
                                      <Video className="w-3 h-3" />
                                      Vidéo
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => togglePublishStatus(pub.id)}
                                  className={`p-2 rounded ${pub.is_published ? 'text-oar-green hover:bg-green-50' : 'text-oar-orange hover:bg-orange-50'}`}
                                  title={pub.is_published ? 'Dépublier' : 'Publier'}
                                >
                                  {pub.is_published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                </button>
                                <button
                                  onClick={() => openPublicationForm(pub)}
                                  className="p-2 text-oar-blue hover:bg-blue-50 rounded"
                                  title="Modifier"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => deletePublication(pub.id)}
                                  className="p-2 text-oar-red hover:bg-red-50 rounded"
                                  title="Supprimer"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                        {publications.length === 0 && (
                          <div className="text-center py-12 bg-white rounded-lg border border-oar-border">
                            <FileText className="w-12 h-12 text-oar-text-muted mx-auto mb-3" />
                            <p className="text-oar-text-muted">Aucune publication</p>
                            <button
                              onClick={() => openPublicationForm()}
                              className="mt-4 text-oar-blue hover:underline"
                            >
                              Créer votre première publication
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Push Notifications Section */}
                  {activeSection === 'push' && (
                    <div className="space-y-6">
                      <h2 className="font-heading text-xl text-oar-text-primary flex items-center gap-2">
                        <BellRing className="w-5 h-5 text-oar-orange" />
                        Notifications Push
                      </h2>

                      {/* OneSignal Status */}
                      <div className="bg-white border border-oar-border rounded-lg p-4">
                        <h3 className="font-medium text-oar-text-primary mb-3 flex items-center gap-2">
                          <Bell className="w-4 h-4 text-oar-blue" />
                          Statut OneSignal
                        </h3>
                        <div className="grid md:grid-cols-3 gap-4">
                          <div className="p-3 bg-oar-bg-secondary rounded-lg">
                            <p className="text-xs text-oar-text-muted">Configuration</p>
                            <p className={`font-bold ${pushStats?.configured ? 'text-oar-green' : 'text-oar-red'}`}>
                              {pushStats?.configured ? 'Configuré' : 'Non configuré'}
                            </p>
                          </div>
                          <div className="p-3 bg-oar-bg-secondary rounded-lg">
                            <p className="text-xs text-oar-text-muted">Abonnés Web Push</p>
                            <p className="font-bold text-oar-blue">{pushStats?.subscribers || 0}</p>
                          </div>
                          <div className="p-3 bg-oar-bg-secondary rounded-lg">
                            <p className="text-xs text-oar-text-muted">Notifications envoyées</p>
                            <p className="font-bold text-oar-orange">{pushHistory.length}</p>
                          </div>
                        </div>
                      </div>

                      {/* Send New Notification */}
                      <div className="bg-white border border-oar-border rounded-lg p-6">
                        <h3 className="font-heading text-lg text-oar-text-primary mb-4 flex items-center gap-2">
                          <Send className="w-5 h-5 text-oar-blue" />
                          Envoyer une notification
                        </h3>
                        
                        {/* Predefined Templates */}
                        <div className="mb-6">
                          <label className="block text-sm text-oar-text-secondary mb-2">
                            Modèles prédéfinis
                          </label>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setPushTitle('Nouvelle publication disponible !');
                                setPushMessage('Un nouvel article vient d\'être publié sur le site de l\'OAR. Découvrez-le maintenant !');
                                setPushUrl('https://russophonie-afrique.org/publications');
                              }}
                              className="px-3 py-1.5 bg-oar-blue-soft text-oar-blue text-sm rounded-lg hover:bg-oar-blue hover:text-white transition-all"
                              data-testid="template-publication"
                            >
                              📰 Nouvelle publication
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setPushTitle('Événement à venir !');
                                setPushMessage('Ne manquez pas notre prochain événement. Consultez les détails et inscrivez-vous dès maintenant.');
                                setPushUrl('https://russophonie-afrique.org/programmes');
                              }}
                              className="px-3 py-1.5 bg-oar-orange-soft text-oar-orange text-sm rounded-lg hover:bg-oar-orange hover:text-white transition-all"
                              data-testid="template-event"
                            >
                              📅 Événement à venir
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setPushTitle('Journée Africaine de la Russophonie - 26 Septembre');
                                setPushMessage('Rejoignez-nous pour célébrer la Journée Africaine de la Russophonie ! Découvrez le programme des festivités.');
                                setPushUrl('https://russophonie-afrique.org/media-kit');
                              }}
                              className="px-3 py-1.5 bg-oar-red-soft text-oar-red text-sm rounded-lg hover:bg-oar-red hover:text-white transition-all"
                              data-testid="template-russophony-day"
                            >
                              🎉 Journée Russophonie
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setPushTitle('Bienvenue aux nouveaux membres !');
                                setPushMessage('L\'OAR accueille de nouveaux membres dans sa communauté. Ensemble, renforçons les liens Afrique-Russie.');
                                setPushUrl('https://russophonie-afrique.org/adhesion');
                              }}
                              className="px-3 py-1.5 bg-oar-green-soft text-oar-green text-sm rounded-lg hover:bg-oar-green hover:text-white transition-all"
                              data-testid="template-welcome"
                            >
                              👋 Bienvenue membres
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setPushTitle('Appel à candidatures - Bourses d\'études');
                                setPushMessage('Les inscriptions pour les bourses d\'études en Russie sont ouvertes. Postulez dès maintenant !');
                                setPushUrl('https://russophonie-afrique.org/candidature');
                              }}
                              className="px-3 py-1.5 bg-purple-100 text-purple-700 text-sm rounded-lg hover:bg-purple-600 hover:text-white transition-all"
                              data-testid="template-scholarship"
                            >
                              🎓 Bourses d'études
                            </button>
                          </div>
                        </div>
                        
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm text-oar-text-secondary mb-1">
                              Titre de la notification *
                            </label>
                            <input
                              type="text"
                              value={pushTitle}
                              onChange={(e) => setPushTitle(e.target.value)}
                              placeholder="Ex: Nouvelle publication disponible !"
                              className="w-full border border-oar-border rounded-lg px-4 py-2 focus:border-oar-blue focus:outline-none"
                              data-testid="push-title-input"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-oar-text-secondary mb-1">
                              Message *
                            </label>
                            <textarea
                              value={pushMessage}
                              onChange={(e) => setPushMessage(e.target.value)}
                              placeholder="Contenu de la notification..."
                              rows={3}
                              className="w-full border border-oar-border rounded-lg px-4 py-2 focus:border-oar-blue focus:outline-none resize-none"
                              data-testid="push-message-input"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-oar-text-secondary mb-1">
                              URL de redirection (optionnel)
                            </label>
                            <input
                              type="url"
                              value={pushUrl}
                              onChange={(e) => setPushUrl(e.target.value)}
                              placeholder="https://russophonie-afrique.org/publications"
                              className="w-full border border-oar-border rounded-lg px-4 py-2 focus:border-oar-blue focus:outline-none"
                              data-testid="push-url-input"
                            />
                            <p className="text-xs text-oar-text-muted mt-1">
                              L'utilisateur sera redirigé vers cette URL en cliquant sur la notification
                            </p>
                          </div>
                          <button
                            onClick={sendPushNotification}
                            disabled={sendingPush || !pushStats?.configured}
                            className="flex items-center gap-2 bg-oar-orange hover:bg-oar-orange-dark disabled:bg-gray-400 text-white px-6 py-2 rounded-lg transition-all"
                            data-testid="send-push-btn"
                          >
                            {sendingPush ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <BellRing className="w-4 h-4" />
                            )}
                            {sendingPush ? 'Envoi en cours...' : 'Envoyer la notification'}
                          </button>
                          {!pushStats?.configured && (
                            <p className="text-sm text-oar-red flex items-center gap-1">
                              <AlertCircle className="w-4 h-4" />
                              OneSignal n'est pas configuré. Vérifiez les clés API dans le backend.
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Notification History */}
                      <div className="bg-white border border-oar-border rounded-lg overflow-hidden">
                        <div className="px-4 py-3 bg-oar-bg-secondary border-b border-oar-border">
                          <h3 className="font-heading text-lg text-oar-text-primary flex items-center gap-2">
                            <History className="w-4 h-4" />
                            Historique des notifications ({pushHistory.length})
                          </h3>
                        </div>
                        <div className="divide-y divide-oar-border max-h-96 overflow-y-auto">
                          {pushHistory.map((notif) => (
                            <div key={notif.id} className="p-4 hover:bg-oar-bg-secondary/50">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className={`w-2 h-2 rounded-full ${notif.status === 'sent' ? 'bg-oar-green' : 'bg-oar-red'}`}></span>
                                    <h4 className="font-medium text-oar-text-primary">{notif.title}</h4>
                                  </div>
                                  <p className="text-sm text-oar-text-secondary mb-2">{notif.message}</p>
                                  {notif.url && (
                                    <a 
                                      href={notif.url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-xs text-oar-blue hover:underline flex items-center gap-1"
                                    >
                                      <ExternalLink className="w-3 h-3" />
                                      {notif.url}
                                    </a>
                                  )}
                                </div>
                                <div className="text-right">
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    notif.status === 'sent' 
                                      ? 'bg-oar-green-soft text-oar-green' 
                                      : 'bg-oar-red-soft text-oar-red'
                                  }`}>
                                    {notif.status === 'sent' ? 'Envoyé' : 'Échec'}
                                  </span>
                                  <p className="text-xs text-oar-text-muted mt-1">
                                    {notif.recipients || 0} destinataires
                                  </p>
                                  <p className="text-xs text-oar-text-muted">
                                    {formatDate(notif.created_at)}
                                  </p>
                                </div>
                              </div>
                              {notif.errors && notif.errors.length > 0 && (
                                <p className="text-xs text-oar-red mt-2">
                                  Erreur: {notif.errors.join(', ')}
                                </p>
                              )}
                            </div>
                          ))}
                          {pushHistory.length === 0 && (
                            <div className="text-center py-8 text-oar-text-muted">
                              <BellRing className="w-12 h-12 mx-auto mb-2 opacity-50" />
                              <p>Aucune notification envoyée</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Crypto Payments Section */}
                  {activeSection === 'crypto' && (
                    <div className="space-y-6">
                      {/* Pending Payments */}
                      <div className="space-y-4">
                        <h2 className="font-heading text-xl text-oar-text-primary flex items-center gap-2">
                          <Clock className="w-5 h-5 text-oar-orange" />
                          Paiements Crypto en attente ({cryptoPayments.length})
                        </h2>
                        
                        <div className="space-y-4">
                          {cryptoPayments.map((payment) => (
                            <div key={payment.id} className="bg-white border border-oar-orange rounded-lg p-4">
                              <div className="flex items-center justify-between mb-4">
                                <div>
                                  <p className="font-medium text-oar-text-primary">{payment.member_name}</p>
                                  <p className="text-sm text-oar-text-muted">{payment.member_email}</p>
                                  <p className="text-xs text-oar-blue">{payment.member_number}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-2xl font-heading font-bold text-oar-green">
                                    {payment.amount} {payment.currency}
                                  </p>
                                  <p className="text-xs text-oar-text-muted">{payment.network}</p>
                                </div>
                              </div>
                              
                              <div className="bg-oar-bg-secondary p-3 rounded mb-4">
                                <p className="text-xs text-oar-text-muted mb-1">Hash de transaction:</p>
                                <div className="flex items-center gap-2">
                                  <code className="text-xs text-oar-blue font-mono break-all flex-1">
                                    {payment.transaction_hash}
                                  </code>
                                  <button
                                    onClick={() => copyToClipboard(payment.transaction_hash)}
                                    className="p-1 hover:text-oar-blue"
                                  >
                                    {copiedText === payment.transaction_hash ? (
                                      <Check className="w-4 h-4 text-oar-green" />
                                    ) : (
                                      <Copy className="w-4 h-4" />
                                    )}
                                  </button>
                                  <button
                                    onClick={() => window.open(`https://tronscan.org/#/transaction/${payment.transaction_hash}`, '_blank')}
                                    className="p-1 hover:text-oar-blue"
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                              
                              <div className="flex gap-2">
                                <button
                                  onClick={() => verifyCrypto(payment.id)}
                                  disabled={processingId === payment.id}
                                  className="flex-1 flex items-center justify-center gap-2 bg-oar-green text-white py-2 rounded hover:bg-oar-green-dark disabled:opacity-50"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                  Valider
                                </button>
                                <button
                                  onClick={() => rejectCrypto(payment.id)}
                                  disabled={processingId === payment.id}
                                  className="flex-1 flex items-center justify-center gap-2 bg-oar-red text-white py-2 rounded hover:bg-oar-red-dark disabled:opacity-50"
                                >
                                  <XCircle className="w-4 h-4" />
                                  Rejeter
                                </button>
                              </div>
                            </div>
                          ))}
                          {cryptoPayments.length === 0 && (
                            <div className="text-center py-8 text-oar-text-muted bg-white rounded-lg border border-oar-border">
                              <CheckCircle className="w-12 h-12 text-oar-green mx-auto mb-2" />
                              <p>Aucun paiement en attente</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Payment History */}
                      <div className="space-y-4">
                        <h2 className="font-heading text-xl text-oar-text-primary flex items-center gap-2">
                          <History className="w-5 h-5 text-oar-blue" />
                          Historique des Paiements ({cryptoHistory.length})
                        </h2>
                        
                        {cryptoHistory.length > 0 ? (
                          <div className="bg-white rounded-lg border border-oar-border overflow-hidden">
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead className="bg-oar-bg-secondary">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-oar-text-secondary uppercase tracking-wider">
                                      Date & Heure
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-oar-text-secondary uppercase tracking-wider">
                                      Membre
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-oar-text-secondary uppercase tracking-wider">
                                      Matricule
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-oar-text-secondary uppercase tracking-wider">
                                      Montant
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-oar-text-secondary uppercase tracking-wider">
                                      Hash Transaction
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-oar-text-secondary uppercase tracking-wider">
                                      Statut
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-oar-text-secondary uppercase tracking-wider">
                                      Traité par
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-oar-border">
                                  {cryptoHistory.map((payment) => (
                                    <tr key={payment.id} className="hover:bg-oar-bg-secondary/50">
                                      <td className="px-4 py-3 whitespace-nowrap">
                                        <div className="text-sm text-oar-text-primary">
                                          {payment.verified_at ? new Date(payment.verified_at).toLocaleDateString('fr-FR') : '-'}
                                        </div>
                                        <div className="text-xs text-oar-text-muted">
                                          {payment.verified_at ? new Date(payment.verified_at).toLocaleTimeString('fr-FR') : '-'}
                                        </div>
                                      </td>
                                      <td className="px-4 py-3">
                                        <div className="text-sm font-medium text-oar-text-primary">
                                          {payment.member_name}
                                        </div>
                                        <div className="text-xs text-oar-text-muted">
                                          {payment.member_email}
                                        </div>
                                      </td>
                                      <td className="px-4 py-3">
                                        <span className="text-xs font-mono text-oar-blue">
                                          {payment.member_number || '-'}
                                        </span>
                                      </td>
                                      <td className="px-4 py-3">
                                        <span className="text-sm font-semibold text-oar-green">
                                          {payment.amount} {payment.currency}
                                        </span>
                                        <div className="text-xs text-oar-text-muted">
                                          {payment.network}
                                        </div>
                                      </td>
                                      <td className="px-4 py-3">
                                        <div className="flex items-center gap-1">
                                          <code className="text-xs font-mono text-oar-text-secondary max-w-[120px] truncate">
                                            {payment.transaction_hash}
                                          </code>
                                          <button
                                            onClick={() => copyToClipboard(payment.transaction_hash)}
                                            className="p-1 hover:text-oar-blue"
                                            title="Copier"
                                          >
                                            {copiedText === payment.transaction_hash ? (
                                              <Check className="w-3 h-3 text-oar-green" />
                                            ) : (
                                              <Copy className="w-3 h-3" />
                                            )}
                                          </button>
                                          <button
                                            onClick={() => window.open(`https://tronscan.org/#/transaction/${payment.transaction_hash}`, '_blank')}
                                            className="p-1 hover:text-oar-blue"
                                            title="Voir sur TronScan"
                                          >
                                            <ExternalLink className="w-3 h-3" />
                                          </button>
                                        </div>
                                      </td>
                                      <td className="px-4 py-3">
                                        {payment.status === 'verified' ? (
                                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            <CheckCircle className="w-3 h-3" />
                                            Approuvé
                                          </span>
                                        ) : (
                                          <div>
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                              <XCircle className="w-3 h-3" />
                                              Rejeté
                                            </span>
                                            {payment.rejection_reason && (
                                              <div className="text-xs text-oar-text-muted mt-1">
                                                {payment.rejection_reason}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-oar-text-secondary">
                                        {payment.verified_by || 'Admin'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-8 text-oar-text-muted bg-white rounded-lg border border-oar-border">
                            <History className="w-12 h-12 text-oar-text-muted mx-auto mb-2" />
                            <p>Aucun historique de paiement</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Emails Section */}
                  {activeSection === 'emails' && (
                    <div className="space-y-4">
                      <h2 className="font-heading text-xl text-oar-text-primary">
                        File d'attente emails ({emails.length})
                      </h2>
                      <p className="text-oar-text-muted text-sm">
                        Système d'emails simulé. Intégration OneSignal en attente de configuration.
                      </p>
                      
                      <div className="bg-white border border-oar-border rounded-lg overflow-hidden">
                        <table className="w-full">
                          <thead className="bg-oar-bg-secondary">
                            <tr>
                              <th className="text-left px-4 py-3 text-xs text-oar-text-muted uppercase">Destinataire</th>
                              <th className="text-left px-4 py-3 text-xs text-oar-text-muted uppercase">Sujet</th>
                              <th className="text-left px-4 py-3 text-xs text-oar-text-muted uppercase">Type</th>
                              <th className="text-left px-4 py-3 text-xs text-oar-text-muted uppercase">Statut</th>
                              <th className="text-left px-4 py-3 text-xs text-oar-text-muted uppercase">Date</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-oar-border">
                            {emails.map((email) => (
                              <tr key={email.id} className="hover:bg-oar-bg-secondary/50">
                                <td className="px-4 py-3 text-sm text-oar-text-primary">{email.recipient_email}</td>
                                <td className="px-4 py-3 text-sm text-oar-text-secondary">{email.subject}</td>
                                <td className="px-4 py-3">
                                  <span className="px-2 py-0.5 bg-oar-blue-soft text-oar-blue text-xs rounded-full">
                                    {email.email_type}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                                    email.status === 'sent' ? 'bg-oar-green-soft text-oar-green' : 'bg-oar-orange-soft text-oar-orange'
                                  }`}>
                                    {email.status}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-xs text-oar-text-muted">{formatDate(email.created_at)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {emails.length === 0 && (
                          <div className="text-center py-8 text-oar-text-muted">Aucun email en file d'attente</div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Assets Section */}
                  {activeSection === 'assets' && (
                    <div className="space-y-6">
                      <h2 className="font-heading text-xl text-oar-text-primary">Ressources graphiques</h2>
                      
                      {/* Matricule Format Info */}
                      <div className="bg-white border border-oar-border rounded-lg p-6">
                        <h3 className="font-heading text-lg text-oar-text-primary mb-4">
                          Format du Matricule
                        </h3>
                        <div className="bg-oar-blue-soft border border-oar-blue/30 rounded-lg p-4 mb-4">
                          <p className="text-oar-blue font-mono text-lg font-bold mb-2">
                            XXX + JJMMAA + CODE_PAYS + V
                          </p>
                          <p className="text-oar-text-secondary text-sm">
                            Exemple: <span className="font-mono font-bold text-oar-orange">DUP151225FRP</span>
                          </p>
                        </div>
                        <ul className="space-y-2 text-sm text-oar-text-secondary">
                          <li className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-oar-blue rounded-full"></span>
                            <span><strong>XXX</strong>: 3 premières lettres du NOM de famille</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-oar-orange rounded-full"></span>
                            <span><strong>JJMMAA</strong>: Date d'inscription (jour/mois/année)</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-oar-green rounded-full"></span>
                            <span><strong>CODE_PAYS</strong>: Code ISO du pays (2 lettres)</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-oar-red rounded-full"></span>
                            <span><strong>V</strong>: Première lettre de la ville</span>
                          </li>
                        </ul>
                      </div>
                      
                      {/* Logo Journée */}
                      <div className="bg-white border border-oar-border rounded-lg p-6">
                        <h3 className="font-heading text-lg text-oar-text-primary mb-4">
                          Logo - Journée Africaine de la Russophonie
                        </h3>
                        <div className="flex items-start gap-6">
                          <img
                            src={JOURNEE_LOGO}
                            alt="Logo Journée Africaine de la Russophonie"
                            className="w-48 h-48 object-contain border border-oar-border rounded-lg"
                          />
                          <div>
                            <p className="text-oar-text-secondary text-sm mb-4">
                              Logo officiel pour l'événement annuel du 26 septembre.
                            </p>
                            <div className="flex items-center gap-3">
                              <a
                                href={JOURNEE_LOGO}
                                download="logo-journee-russophonie.png"
                                className="inline-flex items-center gap-2 bg-oar-blue text-white px-4 py-2 rounded-lg hover:bg-oar-blue-dark"
                              >
                                <Download className="w-4 h-4" />
                                Télécharger PNG
                              </a>
                              <a
                                href="/media-kit"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 bg-oar-orange text-white px-4 py-2 rounded-lg hover:bg-oar-orange-dark"
                              >
                                <FileText className="w-4 h-4" />
                                Kit Média Complet
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Member Card Template */}
                      <div className="bg-white border border-oar-border rounded-lg p-6">
                        <h3 className="font-heading text-lg text-oar-text-primary mb-4">
                          Carte de membre numérique
                        </h3>
                        <p className="text-oar-text-secondary text-sm mb-4">
                          Chaque membre actif dispose d'une carte numérique avec QR code unique et matricule.
                        </p>
                        <div className="grid md:grid-cols-2 gap-4 mb-4">
                          <div className="bg-oar-bg-secondary rounded-lg p-3">
                            <p className="text-xs text-oar-text-muted mb-1">Télécharger la carte</p>
                            <code className="text-xs text-oar-blue">/api/members/[ID]/card</code>
                          </div>
                          <div className="bg-oar-bg-secondary rounded-lg p-3">
                            <p className="text-xs text-oar-text-muted mb-1">Télécharger le certificat PDF</p>
                            <code className="text-xs text-oar-blue">/api/members/[ID]/certificate</code>
                          </div>
                        </div>
                        <div className="bg-gradient-to-br from-oar-blue to-oar-blue-dark text-white rounded-lg p-6 max-w-sm">
                          <div className="flex items-center justify-between mb-4">
                            <span className="text-xs uppercase tracking-wider opacity-80">Carte de Membre</span>
                            <span className="text-xs bg-oar-red px-2 py-0.5 rounded">PREMIUM</span>
                          </div>
                          <p className="font-heading text-xl mb-1">Jean DUPONT</p>
                          <p className="text-sm opacity-80 font-mono font-bold text-oar-orange">DUP151225CMY</p>
                          <div className="flex items-end justify-between mt-4">
                            <div>
                              <p className="text-xs opacity-60">Pays</p>
                              <p className="text-sm">Yaoundé, Cameroun</p>
                            </div>
                            <div className="w-16 h-16 bg-white rounded flex items-center justify-center">
                              <QrCode className="w-12 h-12 text-oar-blue" />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Certificate Template */}
                      <div className="bg-white border border-oar-border rounded-lg p-6">
                        <h3 className="font-heading text-lg text-oar-text-primary mb-4">
                          Certificat de membre (PDF)
                        </h3>
                        <p className="text-oar-text-secondary text-sm mb-4">
                          Un certificat officiel PDF en format A4 paysage est généré automatiquement pour chaque membre actif.
                          Il contient le nom complet, le matricule, le type de membre, le QR code de vérification et les signatures.
                        </p>
                        <div className="bg-[#F5F0E6] border-2 border-oar-gold rounded-lg p-4 max-w-lg">
                          <div className="text-center">
                            <p className="text-oar-blue font-bold text-lg">ORGANISATION AFRICAINE DE LA RUSSOPHONIE</p>
                            <p className="text-oar-red font-semibold mt-2">CERTIFICAT DE MEMBRE</p>
                            <div className="my-4 h-px bg-oar-gold"></div>
                            <p className="text-oar-text-muted text-sm">Nous certifions que</p>
                            <p className="text-oar-blue font-bold text-xl my-2">JEAN DUPONT</p>
                            <p className="text-oar-orange font-mono font-bold">Matricule: DUP151225CMY</p>
                            <p className="text-oar-text-secondary text-sm mt-2">MEMBRE PREMIUM</p>
                          </div>
                        </div>
                      </div>

                      {/* OneSignal Config */}
                      <div className="bg-white border border-oar-border rounded-lg p-6">
                        <h3 className="font-heading text-lg text-oar-text-primary mb-4">
                          Configuration OneSignal
                        </h3>
                        {pushStats?.configured ? (
                          <div className="bg-oar-green-soft border border-oar-green/30 rounded-lg p-4">
                            <p className="text-oar-green font-medium mb-2 flex items-center gap-2">
                              <CheckCircle className="w-5 h-5" />
                              OneSignal est configuré
                            </p>
                            <div className="grid grid-cols-2 gap-4 mt-3">
                              <div>
                                <p className="text-oar-text-muted text-xs">Application</p>
                                <p className="text-oar-text-primary font-medium">{pushStats.app_name || 'OAR Connect'}</p>
                              </div>
                              <div>
                                <p className="text-oar-text-muted text-xs">Abonnés Push</p>
                                <p className="text-oar-text-primary font-medium">{pushStats.subscribers || 0}</p>
                              </div>
                            </div>
                            <p className="text-oar-text-secondary text-sm mt-3">
                              <strong>Note :</strong> L'envoi d'emails via OneSignal nécessite une activation par leur support.
                              Les notifications push fonctionnent normalement.
                            </p>
                          </div>
                        ) : (
                          <div className="bg-oar-orange-soft border border-oar-orange/30 rounded-lg p-4">
                            <p className="text-oar-orange font-medium mb-2">En attente de configuration</p>
                            <p className="text-oar-text-secondary text-sm">
                              Pour activer l'envoi de notifications, configurez vos clés API OneSignal dans le backend.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Map Section */}
                  {activeSection === 'map' && membersByCountry && (
                    <div className="space-y-6">
                      <div className="flex items-center justify-between">
                        <h2 className="font-heading text-xl text-oar-text-primary">
                          Carte des Membres par Pays
                        </h2>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setMapFilter('all')}
                            className={`px-3 py-1.5 rounded-lg text-sm ${mapFilter === 'all' ? 'bg-oar-blue text-white' : 'bg-oar-bg-secondary text-oar-text-secondary'}`}
                          >
                            Tous ({membersByCountry.total_members})
                          </button>
                          <button
                            onClick={() => setMapFilter('premium')}
                            className={`px-3 py-1.5 rounded-lg text-sm ${mapFilter === 'premium' ? 'bg-oar-red text-white' : 'bg-oar-bg-secondary text-oar-text-secondary'}`}
                          >
                            Premium
                          </button>
                          <button
                            onClick={() => setMapFilter('standard')}
                            className={`px-3 py-1.5 rounded-lg text-sm ${mapFilter === 'standard' ? 'bg-oar-green text-white' : 'bg-oar-bg-secondary text-oar-text-secondary'}`}
                          >
                            Standard
                          </button>
                        </div>
                      </div>
                      
                      {/* Stats */}
                      <div className="grid grid-cols-3 gap-4">
                        <div className="bg-white border border-oar-border rounded-lg p-4 text-center">
                          <p className="text-3xl font-heading font-bold text-oar-blue">{membersByCountry.total_members}</p>
                          <p className="text-oar-text-muted text-sm">Membres actifs</p>
                        </div>
                        <div className="bg-white border border-oar-border rounded-lg p-4 text-center">
                          <p className="text-3xl font-heading font-bold text-oar-green">{membersByCountry.total_countries}</p>
                          <p className="text-oar-text-muted text-sm">Pays représentés</p>
                        </div>
                        <div className="bg-white border border-oar-border rounded-lg p-4 text-center">
                          <p className="text-3xl font-heading font-bold text-oar-orange">54</p>
                          <p className="text-oar-text-muted text-sm">Pays en Afrique</p>
                        </div>
                      </div>
                      
                      {/* Country List */}
                      <div className="bg-white border border-oar-border rounded-lg overflow-hidden">
                        <div className="px-4 py-3 bg-oar-bg-secondary border-b border-oar-border">
                          <h3 className="font-heading text-lg text-oar-text-primary">
                            Répartition par pays
                          </h3>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                          {Object.entries(membersByCountry.countries).map(([code, data]) => {
                            const displayCount = mapFilter === 'all' 
                              ? data.total 
                              : mapFilter === 'premium' 
                                ? data.premium 
                                : data.standard;
                            
                            if (mapFilter !== 'all' && displayCount === 0) return null;
                            
                            return (
                              <div key={code} className="bg-oar-bg-secondary rounded-lg p-4 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-oar-blue rounded-full flex items-center justify-center text-white font-bold text-sm">
                                    {code}
                                  </div>
                                  <div>
                                    <p className="font-medium text-oar-text-primary">{data.name}</p>
                                    <div className="flex items-center gap-2 text-xs">
                                      <span className="text-oar-green">{data.standard} std</span>
                                      <span className="text-oar-text-muted">•</span>
                                      <span className="text-oar-red">{data.premium} prm</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-2xl font-heading font-bold text-oar-blue">
                                    {displayCount}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      
                      {/* Visual Map */}
                      <div className="bg-white border border-oar-border rounded-lg p-6">
                        <h3 className="font-heading text-lg text-oar-text-primary mb-4">
                          Carte d'Afrique
                        </h3>
                        <div className="relative bg-oar-bg-secondary rounded-lg p-8 min-h-[400px]">
                          {/* Simplified Africa map representation */}
                          <div className="grid grid-cols-6 gap-2">
                            {Object.entries(membersByCountry.countries).map(([code, data]) => (
                              <div
                                key={code}
                                className="relative group"
                                style={{
                                  transform: `scale(${0.8 + (data.total * 0.1)})`
                                }}
                              >
                                <div className={`
                                  w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xs
                                  ${data.premium > 0 ? 'bg-gradient-to-br from-oar-blue to-oar-red' : 'bg-oar-blue'}
                                  shadow-lg hover:scale-110 transition-transform cursor-pointer
                                `}>
                                  {code}
                                </div>
                                <div className="absolute -bottom-1 right-0 bg-oar-orange text-white text-xs px-1.5 rounded-full">
                                  {data.total}
                                </div>
                                {/* Tooltip */}
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                  <div className="bg-oar-text-primary text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                                    {data.name}: {data.total} membres
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          <p className="text-center text-oar-text-muted text-sm mt-6">
                            Survolez les pays pour voir les détails
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Settings Section */}
                  {activeSection === 'settings' && (
                    <div className="space-y-6">
                      <h2 className="font-heading text-xl text-oar-text-primary">Paramètres</h2>
                      
                      {/* Change Password */}
                      <div className="bg-white border border-oar-border rounded-lg p-6">
                        <h3 className="font-heading text-lg text-oar-text-primary mb-4 flex items-center gap-2">
                          <Key className="w-5 h-5 text-oar-blue" />
                          Changer le mot de passe
                        </h3>
                        <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
                          <div>
                            <label className="block text-sm text-oar-text-secondary mb-1">Mot de passe actuel</label>
                            <input
                              type="password"
                              value={currentPassword}
                              onChange={(e) => setCurrentPassword(e.target.value)}
                              className="w-full border border-oar-border rounded-lg px-4 py-2"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-oar-text-secondary mb-1">Nouveau mot de passe</label>
                            <input
                              type="password"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              placeholder="Minimum 8 caractères"
                              className="w-full border border-oar-border rounded-lg px-4 py-2"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-oar-text-secondary mb-1">Confirmer le mot de passe</label>
                            <input
                              type="password"
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              className="w-full border border-oar-border rounded-lg px-4 py-2"
                              required
                            />
                          </div>
                          <button
                            type="submit"
                            disabled={changingPassword}
                            className="flex items-center gap-2 bg-oar-blue hover:bg-oar-blue-dark disabled:bg-gray-400 text-white px-6 py-2 rounded-lg"
                          >
                            {changingPassword ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
                            {changingPassword ? 'Modification...' : 'Modifier le mot de passe'}
                          </button>
                        </form>
                      </div>
                      
                      {/* Security Info */}
                      <div className="bg-white border border-oar-border rounded-lg p-6">
                        <h3 className="font-heading text-lg text-oar-text-primary mb-4 flex items-center gap-2">
                          <Shield className="w-5 h-5 text-oar-green" />
                          Sécurité
                        </h3>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between p-3 bg-oar-bg-secondary rounded-lg">
                            <div>
                              <p className="font-medium text-oar-text-primary">Authentification JWT</p>
                              <p className="text-xs text-oar-text-muted">Token sécurisé avec expiration 24h</p>
                            </div>
                            <span className="px-2 py-1 bg-oar-green-soft text-oar-green text-xs rounded-full">Actif</span>
                          </div>
                          <div className="flex items-center justify-between p-3 bg-oar-bg-secondary rounded-lg">
                            <div>
                              <p className="font-medium text-oar-text-primary">Email de récupération</p>
                              <p className="text-xs text-oar-text-muted">emileparfait2003@gmail.com</p>
                            </div>
                            <span className="px-2 py-1 bg-oar-blue-soft text-oar-blue text-xs rounded-full">Configuré</span>
                          </div>
                          <div className="flex items-center justify-between p-3 bg-oar-bg-secondary rounded-lg">
                            <div>
                              <p className="font-medium text-oar-text-primary">Hashage mot de passe</p>
                              <p className="text-xs text-oar-text-muted">BCrypt avec salt</p>
                            </div>
                            <span className="px-2 py-1 bg-oar-green-soft text-oar-green text-xs rounded-full">Sécurisé</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </main>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AdminPage;
